// Thin client over the gateway HTTP API. Cookie auth - we just send
// `credentials: 'include'`. All non-2xx responses throw `ApiError` so
// callers can branch on error.kind ('unauthorized' / 'forbidden' / etc.).


// Types imported from the generated wire-format definitions (api-types.gen.ts)
// and re-exported for backward compatibility - consumers still import from './api'.
import type {
  ActionAppliesWhen,
  ActionKind,
  ActionPhase,
  ActionPriority,
  ActionSource,
  ActionTargetKind,
  ActionTargetRole,
} from '@/lib/actionItems';
import type {
  AdminUser,
  AgentBundle,
  AgentBundleMachines,
  AgentBundleStatus,
  CapacityPolicyEdit,
  CapacityPolicyList,
  CapacityPolicyRow,
  CaptureCompleteness,
  Classification,
  ConfidenceLevel,
  ConfigOptionsResponse,
  CreateSSOUserRequest,
  EgressCredential,
  EgressSettings,
  EgressStatus,
  EventBundleFamily,
  EventBundleProcess,
  EventBundleProcessIndex,
  EventBundleResponse,
  EventFamily,
  EventQueryResponse,
  EventRow,
  EventSourceTotals,
  EventStreamCursor,
  EventStreamEvent,
  EventStreamResponse,
  EventSummaryResponse,
  EvidenceKind,
  HypervisorSetupStatusResponse as GenHypervisorSetupStatusResponse,
  Org as GenOrg,
  HypervisorType,
  Indicator,
  InflightResponse,
  IntelActivityResponse,
  IntelLocalAnalysis,
  IntelQuery,
  IntelSearchResponse,
  IntelSourceStatus,
  IntelTally,
  JobCapability,
  OfferedSSO,
  OrgInflight,
  OSFamily,
  OveragePolicy,
  PAWorkerState,
  PaworkerShape,
  PostAnalysisStatsResponse,
  ProviderClaimCode,
  ProviderConfigDelivery,
  ProviderPaworkers,
  ReportArtifact,
  RunTag,
  SCIMAllowlistRequest,
  SCIMConfig,
  SCIMToken,
  SetProviderPaworkersResponse,
  SetupPreflightResponse,
  SSOConfig,
  SSOConfigRequest,
  SSOEndpoints,
  SSOMode,
  TaskFailureStage,
  TaskRouting,
  Ttp,
  UsagePeriod,
} from './api-types.gen';
import { VM_LANE } from './lanes';
import type {
  WireAnalyzerHosts,
  WireBehaviorSection,
  WireMlSection,
  WireReportResponse,
  WireSubmissionDetail,
} from './wire-types';

export type {
  AgentBundle,
  AgentBundleMachines,
  AgentBundleStatus,
  CapacityPolicyEdit,
  CapacityPolicyList,
  CapacityPolicyRow,
  CaptureCompleteness,
  Classification,
  EventBundleFamily,
  EventBundleProcess,
  EventBundleProcessIndex,
  EventBundleResponse,
  EventFamily,
  EventQueryResponse,
  EventRow,
  EventSourceTotals,
  EventStreamCursor,
  EventStreamEvent,
  EventStreamResponse,
  EventSummaryResponse,
  EvidenceKind,
  HypervisorType,
  Indicator,
  InflightResponse,
  IntelActivityResponse,
  IntelLocalAnalysis,
  IntelQuery,
  IntelSearchResponse,
  IntelSourceStatus,
  IntelTally,
  OrgInflight,
  OSFamily,
  OveragePolicy,
  PaworkerShape,
  ProviderClaimCode,
  ProviderConfigDelivery,
  ProviderPaworkers,
  SetProviderPaworkersResponse,
  TaskFailureStage,
  Ttp,
  UsagePeriod,
};

type Confidence = ConfidenceLevel;
type PaworkerState = PAWorkerState;
type PAJobCapability = JobCapability;

export class ApiError extends Error {
  constructor(
    public status: number,
    public kind: ApiErrorKind,
    message: string,
    public fieldErrors?: Record<string, string[]>,
    public raw?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * The machine-readable `code` a refusal carries beside its message, such as
 * `sso_required`, or undefined when it carries none.
 */
export function apiErrorCode(err: unknown): string | undefined {
  if (!(err instanceof ApiError)) return undefined;
  const raw = err.raw;
  if (raw && typeof raw === 'object' && 'code' in raw && typeof raw.code === 'string') return raw.code;
  return undefined;
}

/**
 * Whose SSO settings a call reads or writes: an org's own portal (the
 * management tier, under the org's tenant context) or the platform admin's
 * surface for any org. The two route families take the same bodies.
 */
export interface SSOScope {
  kind: 'org' | 'admin';
  orgId: string;
}

function ssoBase(scope: SSOScope): string {
  const org = encodeURIComponent(scope.orgId);
  return scope.kind === 'admin' ? `/api/admin/orgs/${org}` : `/api/org/${org}`;
}

/**
 * The body of PUT .../sso-config. client_secret is sent only when one was
 * typed: an absent key keeps the stored secret, which reads never return.
 */
export type SSOConfigSave = Omit<SSOConfigRequest, 'client_secret'> & { client_secret?: string };

/** The address that starts a single sign-on through orgId's identity provider. */
export function ssoStartURL(orgId: string, returnPath: string): string {
  return `/api/auth/sso/${encodeURIComponent(orgId)}/start?return=${encodeURIComponent(returnPath)}`;
}

type ApiErrorKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'validation'
  | 'unsupported_media'
  | 'server'
  | 'unknown';

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 0) return 'network';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  // 415: the file itself is the problem, and the message names the type. The
  // upload page shows it inline rather than in a toast that dismisses itself.
  if (status === 415) return 'unsupported_media';
  if (status === 422) return 'validation';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

// sanitizeTargetName cleans a user-supplied filename used as the submission's
// display name (FE-CORR-02): drop any path component, strip control characters,
// collapse whitespace, and clamp the length. Defensive only - the server
// enforces its own limits; this just prevents a malformed name from garbling
// the UI. Falls back to a neutral label if the name strips to empty.
function sanitizeTargetName(name: string): string {
  // Drop any path component a browser/OS may include in File.name.
  const base = name.split(/[/\\]/).pop() ?? name;
  // Strip ASCII control chars (0x00-0x1F, 0x7F) without a control-byte regex
  // literal, collapse runs of whitespace, then clamp the length.
  let cleaned = '';
  for (const ch of base) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) continue;
    cleaned += ch;
  }
  const clamped = cleaned.replace(/\s+/g, ' ').trim().slice(0, 255).trim();
  return clamped || 'sample';
}

// Map orchestrator task_status values onto the legacy malbox status enum
// the dashboard's StatusBadge / ACTIVE_STATUSES were built around.
// Exported for the consistency test that pins ACTIVE_STATUSES to this map's
// values - the drift it guards against actually happened (see ACTIVE_STATUSES).
export const STATUS_ALIAS: Record<string, Submission['status']> = {
  queued: 'pending',
  dispatching: 'initializing',
  running: 'running',
  uploading: 'uploading',
  finishing: 'stopping',
  complete: 'completed',
  failed: 'failed',
  cancelled: 'canceled',
  expired: 'failed',
};

// Frontend-alias statuses that mean "still in flight" - exactly the
// non-terminal members of Submission['status']. A submission in one of these
// gets the live treatment (spinner, in-flight count, animated row dot) and is
// still polled. Single source shared by every page that distinguishes active
// from settled, so a new active status (e.g. 'uploading') is honored everywhere
// at once instead of being missed on one surface.
// Exactly the values STATUS_ALIAS can produce, minus the terminals - a member
// no alias maps to is a state no submission can ever be in, and one phantom
// (preparing_resources, legacy malbox vocabulary) sat here unreachable for
// months. api.test.ts pins the two vocabularies together.
export const ACTIVE_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'initializing',
  'running',
  'uploading',
  'stopping',
]);

// Loose wire shape the orchestrator emits; kept untyped at the boundary
// and read via pickString so a field-name typo is a compile error.
type SubmissionRow = Record<string, unknown>;

function pickString(row: SubmissionRow, ...keys: string[]): string | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string') return v;
  }
  return undefined;
}

function normalizeSubmission(row: SubmissionRow): Submission {
  // Coalesce orchestrator field names (task_id/task_status/completed_at)
  // into the dashboard's expected ones (malbox_task_id/status/completed_on).
  const rawStatus = pickString(row, 'status', 'task_status');
  return {
    ...row,
    malbox_task_id: row.malbox_task_id ?? row.task_id,
    status: rawStatus ? (STATUS_ALIAS[rawStatus] ?? (rawStatus as Submission['status'])) : null,
    completed_on: pickString(row, 'completed_on', 'completed_at') ?? null,
    // Defaulted rather than left undefined: a row without the field predates
    // the column, and every row that predates it is a file submission.
    target_kind: pickString(row, 'target_kind') ?? 'file',
    // The lane is the field every lane-aware surface branches on, so an
    // undefined one is not a state any of them should have to render. The
    // default is the VM lane because every row that predates the lane is a
    // file detonation, which is the column's own history: migration 0087
    // backfilled exactly that.
    lane: pickString(row, 'lane') ?? VM_LANE,
  } as Submission;
}

// Fan the orchestrator's one flat row from GET /api/submissions/{id}
// into the legacy {submission, task} envelope ReportPage expects.
function normalizeSubmissionDetail(row: WireSubmissionDetail): { submission: Submission; task: TaskDetail } {
  const submission = normalizeSubmission(row as SubmissionRow);
  const rawStatus: string | undefined = row.status ?? row.task_status;
  const task: TaskDetail = {
    id: row.task_id ?? '',
    status: rawStatus ? (STATUS_ALIAS[rawStatus] ?? rawStatus) : 'unknown',
    target: row.target_name ?? '',
    platform: row.platform ?? '',
    timeout: row.timeout_seconds ?? 0,
    created_on: row.enqueued_at ?? row.submitted_at ?? '',
    started_on: row.started_at ?? null,
    started_warm: row.started_warm ?? null,
    completed_on: row.completed_at ?? null,
    stopped_early_on: row.stopped_early_at ?? null,
    stopped_early_by: row.stopped_early_by ?? null,
    result_caveat: row.result_caveat ?? null,
    first_attempt: row.first_attempt
      ? { failure_message: row.first_attempt.failure_message, stopped_on: row.first_attempt.stopped_at ?? null }
      : null,
    analyzer_image_digest: row.analyzer_image_digest ?? null,
    target_kind: row.target_kind ?? 'file',
    // Same default as normalizeSubmission, for the same reason: a row older
    // than the lane is a file detonation.
    lane: row.lane ?? VM_LANE,
    os_family: row.os_family ?? null,
    base_image_name: row.base_image_name ?? null,
    exit_country: row.exit_country ?? null,
    routing: row.routing ?? null,
    launch_options: row.launch_options ?? null,
    sample: row.sample_sha256
      ? {
          file_size: row.sample_size ?? 0,
          // The detonation kind, not a stored MIME type: it is what the guest
          // launches the sample as, which is the thing a reader wants and the
          // one the server actually knows.
          file_type: row.detonation_kind ?? '',
          sha256: row.sample_sha256,
        }
      : undefined,
    failure: row.failure_stage
      ? {
          stage: row.failure_stage as TaskFailureStage,
          message: row.failure_message ?? '',
          diagnostics: row.failure_diagnostics ?? {},
        }
      : undefined,
    result_zip_size_bytes: row.result_zip_size_bytes ?? undefined,
  };
  return { submission, task };
}

/**
 * The ceiling on one API call, in minutes.
 *
 * This is not a service budget. The orchestrator bounds its own work, and the
 * longest of those bounds is the ten-minute remote destroy window in
 * `src/core/hypervisors` - a cloud machine really can take that long to tear
 * down, and cutting the call short would report a failure for an operation that
 * goes on to succeed. The whole request is that window plus the steps around
 * it, failing the machine's in-flight tasks before and recording the outcome
 * after, so the ceiling has to clear ten minutes with room rather than meet it.
 *
 * Above every server-side bound, it fires only for the case none of them cover:
 * a connection that has died without closing, which `fetch` on its own waits
 * out for the life of the tab. That case is why the constant exists. Any
 * control that shows progress while its request is in flight needs the promise
 * to settle eventually, and until there was a deadline here, nothing
 * guaranteed one ever would.
 */
const REQUEST_CEILING_MIN = 15;
const REQUEST_CEILING_MS = REQUEST_CEILING_MIN * 60_000;

/**
 * What a lost answer says.
 *
 * The server detaches its long operations from the request context - a destroy
 * runs on `context.WithoutCancel` under the hypervisor's own budget - so this
 * side hitting its ceiling means the answer went missing, not that the work
 * stopped. The wording has to leave room for it having succeeded without us.
 */
const CEILING_MESSAGE = `No answer in ${REQUEST_CEILING_MIN} min. The operation may still be running - reload to see where it got to.`;

/**
 * Runs one network call under the ceiling, with the caller still able to cancel
 * it early.
 *
 * `AbortSignal.timeout()` would be one line, and it is the wrong line: it hands
 * back no way to cancel the timer, so every completed request leaves a
 * fifteen-minute timer behind it. On a polling page that is hundreds of live
 * signals in a tab that is doing nothing. An owned controller costs three more
 * lines and releases in `finally`.
 *
 * A caller's signal is forwarded into the ceiling rather than replacing it, so
 * a query that unmounts aborts immediately and one that hangs still stops.
 */
async function withDeadline<T>(
  caller: AbortSignal | null | undefined,
  call: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const ceiling = new AbortController();
  const timer = setTimeout(
    () => ceiling.abort(new DOMException(CEILING_MESSAGE, 'TimeoutError')),
    REQUEST_CEILING_MS,
  );
  const forward = () => ceiling.abort(caller?.reason);
  if (caller) {
    if (caller.aborted) forward();
    else caller.addEventListener('abort', forward, { once: true });
  }
  try {
    return await call(ceiling.signal);
  } finally {
    clearTimeout(timer);
    caller?.removeEventListener('abort', forward);
  }
}

/**
 * Reads `name` and `message` off a rejection without asking what it is.
 *
 * `instanceof` answers this badly from both directions. An abort reason is
 * minted by whichever copy of the platform globals owns the AbortController,
 * which need not be the copy this module was loaded against, so
 * `instanceof DOMException` misses a real abort. And a DOMException is not
 * always an Error either - jsdom's does not extend it - so `instanceof Error`
 * misses a real timeout. The two fields are the whole contract this module
 * needs, and every library that has to detect an abort reads them the same way.
 */
function readRejection(e: unknown): { name: string; message: string } {
  let name = '';
  let message = 'network failure';
  if (typeof e === 'object' && e !== null) {
    if ('name' in e && typeof e.name === 'string') name = e.name;
    if ('message' in e && typeof e.message === 'string') message = e.message;
  }
  return { name, message };
}

/** True for the rejections that mean the call was cut short, not answered badly. */
function isCutShort(e: unknown): boolean {
  const { name } = readRejection(e);
  return name === 'AbortError' || name === 'TimeoutError';
}

/**
 * Maps a rejected fetch or body read onto the error contract callers expect.
 *
 * An abort the caller asked for passes through as itself. Every consumer of
 * this module treats an `AbortError` as "that component went away", and
 * dressing one up as a network failure would put a toast on every unmount.
 */
function networkError(e: unknown): unknown {
  const { name, message } = readRejection(e);
  if (name === 'AbortError') return e;
  if (name === 'TimeoutError') return new ApiError(0, 'network', CEILING_MESSAGE);
  return new ApiError(0, 'network', message);
}

/** The reason a refused response's JSON body gives in `error`, as request()
 *  reads it, or fallback when the body gives none. */
async function reasonOf(resp: Response, fallback: string): Promise<string> {
  if (!resp.headers.get('content-type')?.includes('application/json')) return fallback;
  try {
    const raw: unknown = await resp.json();
    if (raw && typeof raw === 'object' && 'error' in raw && typeof raw.error === 'string' && raw.error) {
      return raw.error;
    }
  } catch {
    // A body that is not the JSON it claims to be gives no reason.
  }
  return fallback;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return withDeadline(init.signal, async (signal) => {
    let resp: Response;
    try {
      resp = await fetch(path, {
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          ...(init.body && !(init.body instanceof FormData)
            ? { 'Content-Type': 'application/json' }
            : {}),
          ...(init.headers ?? {}),
        },
        ...init,
        // After the spread on purpose. A caller's own signal is not lost -
        // withDeadline has already folded it into this one - but it must not
        // replace the ceiling either.
        signal,
      });
    } catch (e) {
      throw networkError(e);
    }
    if (!resp.ok) {
      let raw: any = null;
      let message = resp.statusText;
      let fieldErrors: Record<string, string[]> | undefined;
      if (resp.headers.get('content-type')?.includes('application/json')) {
        try {
          raw = await resp.json();
        } catch {}
      }
      if (raw && typeof raw === 'object') {
        if (typeof raw.error === 'string') message = raw.error;
        if (raw.errors && typeof raw.errors === 'object') fieldErrors = raw.errors;
      }
      // Global session-expiry signal: ANY 401 means the session is dead
      // (revoked session_version, logout elsewhere, cleared cookie).
      // AuthContext listens and clears the user so the route guard lands on
      // /login - no stale UI on a dead session. Harmless on already-
      // unauthenticated pages (login/bootstrap: user is already null).
      if (resp.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('voltz:session-expired'));
      }
      throw new ApiError(resp.status, kindFromStatus(resp.status), message, fieldErrors, raw);
    }
    // All current 204 callers use request<void>. The cast is unsound for
    // non-void T but no endpoint that returns 204 is called with a concrete T
    // today.
    if (resp.status === 204) return undefined as T;
    try {
      return (await resp.json()) as T;
    } catch (e) {
      // The ceiling can fire after the headers land and before the body ends,
      // and an abort there rejects the read rather than the fetch. Without this
      // the one call that most needs to settle as an ApiError settles as a bare
      // DOMException instead. Anything else here is a malformed body, which is
      // not a network fault and keeps its own error.
      if (isCutShort(e)) throw networkError(e);
      throw e;
    }
  });
}

// Unwraps the orchestrator's `{<key>: [...]}` array envelope. Tolerates a
// bare array too (legacy gateway), so callers never get the wrapper object
// and crash on `.map`/`.slice`.
/** POST a multipart body with upload progress, and decode the JSON reply.
 *
 * fetch() cannot report upload progress, so the two upload paths share this
 * XMLHttpRequest instead of each growing their own copy. */
function uploadForm<T>(
  path: string,
  form: FormData,
  onProgress?: (loaded: number, total: number) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    xhr.withCredentials = true;

    // A stall guard, not the flat ceiling `request` uses. A sample upload is
    // bounded by the submission size cap but not by time, so a large file on a
    // slow link is a healthy request that simply takes a while and a fixed
    // deadline would kill it. What is never healthy is no byte moving for the
    // whole ceiling, so the timer is re-armed on every progress event. Once the
    // body is up the events stop and the last arming becomes the deadline on
    // the reply, which is the behaviour we want there too.
    //
    // This matters because the submit button reports its own progress the same
    // way a row action does, and fetch's deadline never covered it.
    let stall: ReturnType<typeof setTimeout>;
    const arm = () => {
      clearTimeout(stall);
      stall = setTimeout(() => xhr.abort(), REQUEST_CEILING_MS);
    };

    xhr.upload.onprogress = (e) => {
      arm();
      if (e.lengthComputable && onProgress) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(new ApiError(xhr.status, 'unknown', 'bad json'));
        }
        return;
      }
      let msg = xhr.statusText;
      try {
        const j: unknown = JSON.parse(xhr.responseText);
        if (typeof j === 'object' && j !== null && 'error' in j && typeof j.error === 'string') {
          msg = j.error;
        }
      } catch {
        // Non-JSON error body; the status text is the best available message.
      }
      reject(new ApiError(xhr.status, kindFromStatus(xhr.status), msg));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'upload failed'));
    // Nothing else aborts this request, so an abort is the stall guard firing.
    xhr.onabort = () => reject(new ApiError(0, 'network', CEILING_MESSAGE));
    xhr.onloadend = () => clearTimeout(stall);
    arm();
    xhr.send(form);
  });
}

async function requestArray<T>(path: string, key: string, init: RequestInit = {}): Promise<T[]> {
  const r = await request<T[] | Record<string, unknown>>(path, init);
  if (Array.isArray(r)) return r;
  if (r && typeof r === 'object') {
    const v = (r as Record<string, unknown>)[key];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

/**
 * Renders an event-stream filter as a query string, omitting everything the
 * caller left unset.
 *
 * Omission is load-bearing on two of these. `sources` empty means every source,
 * so sending `sources=` would ask the server for a selection of none; and a
 * cursor's two halves travel together or not at all, because a tick without a
 * row number is not a position in the chronology - the server refuses the pair
 * half-given rather than resuming in the middle of a tick.
 */
function eventStreamQuery(filter: EventStreamFilter): string {
  const params = new URLSearchParams();
  if (filter.sources && filter.sources.length > 0) params.set('sources', filter.sources.join(','));
  if (filter.pid != null) params.set('pid', String(filter.pid));
  if (filter.search) params.set('q', filter.search);
  if (filter.cursor) {
    params.set('cursor_tick', String(filter.cursor.ts_100ns));
    params.set('cursor_seq', String(filter.cursor.seq));
  }
  if (filter.limit != null) params.set('limit', String(filter.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Auth ---------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
  // Freshly-provisioned operators must hit /change-password before anything
  // else; cleared once they submit a new password.
  must_change_password: boolean;
  // Set by the orchestrator when no providers/images/VMs exist yet; the SPA
  // redirects admins to /setup until dismissed.
  setup_required?: boolean;
  created_at: string;
  memberships?: Membership[];
}

// BackupRun mirrors backup.RunStatus: one detached pg_dump's state.
//
// `running` true means the dump is still being written. False is terminal, and
// exactly one of `error` / `location` is set. `location` is a filesystem path
// on a local target and an object key on a remote one, so it is shown verbatim
// rather than parsed.
export interface BackupRun {
  id: string;
  running: boolean;
  started_at: string;
  completed_at?: string;
  error?: string;
  location?: string;
  sha256?: string;
  bytes?: number;
  duration_ms?: number;
}

export interface SetupStatus {
  setup_dismissed: boolean;
  // Fleet OTLP endpoint (http://<ingest>:9428) providers/workers ship logs to,
  // set only when remote logging was configured at install. Empty/absent =
  // no central logging; the install commands then omit --logs-otlp-endpoint.
  logs_otlp_endpoint?: string;
  // Fleet provider-agent image (<registry>/voltz-provider-agent:<tag>) the
  // orchestrator was installed with. Surfaced so the provider install command
  // pulls from the right registry; empty/absent omits --image (public default).
  provider_image?: string;
  providers: { count: number; online: number };
  base_images: { count: number; active: number };
  paworkers: { count: number; online: number };
  pa_jobs: { count: number; served: number };
}

export interface OrchestrationProvider {
  id: string;
  name: string;
  hypervisor: HypervisorType | string;
  enabled: boolean;
  priority: number;
  labels: Record<string, string>;
  // Orchestrator-managed providers run as containers on the orch host.
  managed: boolean;
  container_id?: string;
  managed_ports?: Record<string, number>;
  // Decrypted gRPC dial config ({target, server_name}) - only on
  // single-provider GET; ListProviders omits it to keep secrets off the
  // wire on every poll. Written by enrollment, never by the dashboard.
  config?: Record<string, unknown>;
  // Decrypted hypervisor BACKEND config (API tokens, endpoints, plus
  // setup-written internals like hetzner's image_map). Same single-row-GET
  // rule; absent when the provider has none yet.
  hypervisor_config?: Record<string, unknown>;
  config_version: number;
  // Whether a hypervisor connection config is stored. False → the Connection
  // section reads "Not configured"; the version fields below only matter once true.
  hypervisor_config_set: boolean;
  // Which secret fields currently hold a value. The values themselves are
  // never served: GetProvider redacts every field the registry marks
  // FieldSecret, so an edit dialog shows that a credential exists without
  // being able to read it, and omitting the field on save keeps it.
  hypervisor_config_secrets_set?: Record<string, boolean>;
  // config_version the agent is actually running (its last applied ACK). Equals
  // config_version when the saved connection config is live; trails it in flight.
  hypervisor_config_applied_version: number;
  // config_version the agent terminally refused; a rejection is current only
  // while this equals config_version (a later save moves config_version past it).
  hypervisor_config_rejected_version: number;
  // The agent's operator-facing refusal reason for the current rejection; '' when none.
  hypervisor_config_rejected_reason: string;
  // Egress push state, read like the hypervisor config's: a refusal is
  // current only while egress_rejected_version equals egress_version.
  egress_version: number;
  egress_applied_version: number;
  egress_rejected_version: number;
  egress_reject_reason: string;
  // Admin-managed default vCPU & RAM per VM on this provider. Only
  // `default_*` matter; legacy `min_*`/`max_*` still ship but are ignored.
  spec_bounds?: ProviderDefaultSpecs;
  // Host capacity measured at claim time: every core and byte on the box,
  // whatever else the box runs. Null on pre-migration rows and cloud kinds with
  // no host concept. Context for the budget, not the gate.
  host_cpus?: number | null;
  host_memory_mb?: number | null;
  // The share of that host the operator has allowed Voltz to commit, and the
  // ceiling provisioning is actually gated on. A new enrollment seeds it below
  // the measured host by the headroom the host OS needs, a row that predates the
  // column carries the whole host, and either way it is lowered by hand on a
  // shared box since the platform can only count its own machines. Always a pair
  // or absent - resolve it with capacityCeiling(), never a per-field `??`.
  budget_cpus?: number | null;
  budget_memory_mb?: number | null;
  // Live usage. *_in_use = committed across status='busy' machines.
  // *_committed = every machine except a quarantined one held for vm_missing,
  // which is the sum the capacity guard checks against the budget. A
  // decommissioned machine is a deleted row, so it leaves the sum by itself.
  usage?: ProviderUsage;
  // What this provider's agent last reported about its own host: whether it
  // can run an analysis container. Served on the list and on the single
  // provider GET alike, so a row can show the lane without a second read.
  // False is the agent's actual answer, and it is also what a provider reads
  // as until it has first been asked.
  container_capable?: boolean;
  // Which container engine that agent reported running, from the same report.
  // Absent until one has been made; '' is the agent saying it has no engine
  // configured, which is not the same answer.
  container_engine_kind?: string | null;
  // The container-engine config an operator set for this provider's agent,
  // read like the hypervisor config's: whether one is stored (never the config
  // itself, which carries cloud credentials), the version the latest save
  // produced, and the versions the agent applied or refused. Only a managed
  // provider is delivered one.
  container_engine_kind_desired?: string;
  container_engine_config_set?: boolean;
  container_engine_config_version?: number;
  container_engine_applied_version?: number;
  container_engine_rejected_version?: number;
  // The agent's reason for refusing the current config, '' when none.
  container_engine_rejected_reason?: string;
  // The agent bundle this provider serves to the machines it builds now, null
  // when it serves none. A machine keeps the bundle it booted for as long as it
  // lives, so agent_bundle_machines counts the ones behind this one.
  agent_bundle?: AgentBundle | null;
  agent_bundle_machines?: AgentBundleMachines;
  created_at: string;
  updated_at: string;
}

export interface ProviderUsage {
  busy_count: number;
  ready_count: number;
  total_machines: number;
  cpus_in_use: number;
  memory_mb_in_use: number;
  cpus_committed?: number;
  memory_mb_committed?: number;
}

// Per-provider default VM spec (admin-only). Only `default_*` drive the UI;
// min/max stay optional for older orchestrator builds that still emit them.
interface ProviderDefaultSpecs {
  default_cpus: number;
  default_memory_mb: number;
  min_cpus?: number;
  max_cpus?: number;
  min_memory_mb?: number;
  max_memory_mb?: number;
}

interface OrchestrationProviderPatch {
  name?: string;
  enabled?: boolean;
  priority?: number;
  labels?: Record<string, string>;
  // Hypervisor backend config - the orchestrator validates it against the
  // hypervisor's config_fields and stores it in hypervisor_config.
  config?: Record<string, unknown>;
}

// The updated provider row plus the delivery verdict of a just-saved hypervisor
// config (applied / pending / rejected). `delivery` rides only on a config
// PATCH - a non-config PATCH (rename, enable, priority) omits it.
interface OrchestrationProviderSaveResult extends OrchestrationProvider {
  delivery?: ProviderConfigDelivery;
}

// One row per VM on a provider, matching the orchestrator's
// admin/orchestration/providers/{id}/machines envelope. The leaner
// provider-scoped shape; the malbox-era fields were never wired.
export interface ProviderMachine {
  /** The machine's identity - opaque, unique fleet-wide, and the key every
   *  machine-scoped endpoint takes. */
  id: string;
  name: string;
  /** The hypervisor's own handle. Null until the VM has actually been created. */
  provider_ref: string | null;
  status: string;
  /** Null for a container runner: the schema requires it, so absence is the
   *  row's shape rather than a value that failed to load. */
  base_image_sha256: string | null;
  os_family: OSFamily | null;
  /** Null for a container runner. Its limits live on the analyzer registry row
   *  and are read at dispatch, so these columns would report a default the
   *  runner does not get. */
  cpus: number | null;
  memory_mb: number | null;
  kind: 'vm' | 'container';
  /** The analyzer a container runner serves; null for a VM. */
  analyzer: string | null;
  /** The agent bundle the machine booted; null when nothing recorded one. */
  agent_bundle: AgentBundle | null;
  /** How that bundle compares with the one the provider serves now. Absent for a
   *  container runner, which boots none, and on a provider that serves none. */
  agent_bundle_status?: AgentBundleStatus;
  created_at: string;
}

export interface PostAnalysisWorker {
  worker_id: string;
  name: string;
  enabled: boolean;
  priority: number;
  host_cpus?: number | null;
  host_memory_mb?: number | null;
  // What the worker admits jobs against, from its last hello: jobs that are not
  // network bound at once, and the memory the running jobs may declare. Absent
  // until a worker new enough to state them connects.
  cpu_slots?: number | null;
  memory_budget_mb?: number | null;
  host_labels: string[];
  labels: Record<string, string>;
  capabilities: PAJobCapability[];
  failure_count: number;
  // pending_claim = row exists but installer not yet run; online = enrolled
  // with an open bidi stream; offline = enrolled but not connected.
  state: PaworkerState;
  // Set only while pending_claim; drives the claim-code TTL countdown.
  claim_code_expires_at?: string | null;
  last_seen_at?: string | null;
  enrolled_at: string;
  updated_at: string;
  connected: boolean;
  in_flight?: Record<string, number>;
  // Set on a worker a managed provider runs as a native container, which that
  // provider's worker target owns. native_cpus and native_memory_mb are the
  // target's container shape. All absent on a worker an operator installed.
  native_provider_id?: string | null
  native_provider_name?: string
  native_slot?: number | null
  native_cpus?: number | null
  native_memory_mb?: number | null
}

interface PostAnalysisWorkerCreate {
  name: string;
  enabled?: boolean;
  priority?: number;
  labels?: Record<string, string>;
}

// Response from createPaworker / regenerateClaim: the row plus the
// plaintext-once claim code (only its sha256 is persisted server-side).
export interface PostAnalysisWorkerCreated extends PostAnalysisWorker {
  claim_code: string;
  claim_code_expires_at: string;
  // Enrollment target + trust anchor for the install command. The endpoint is
  // the orchestrator's reachable host:port (not the dashboard URL); the
  // fingerprint is the stable root CA the worker pins via --ca-fingerprint.
  // Omitted by older orchestrators.
  orchestrator_claim_endpoint?: string;
  ca_fingerprint?: string;
}

interface PostAnalysisWorkerPatch {
  name?: string;
  enabled?: boolean;
  priority?: number;
  labels?: Record<string, string>;
}


// Mirrors the orchestrator's jobResponse: a pa_jobs row plus the live
// "which paworkers advertise this job" overlay. The DAG editor topo-sorts
// this list client-side into per-tier columns.
export type PostAnalysisStats = PostAnalysisStatsResponse;

export interface PAJob {
  name: string;
  enabled: boolean;
  timeout_seconds: number;
  retries: number;
  // Hard edges: ordering AND requirement. A job whose hard upstreams are all
  // terminal with none succeeded is skipped.
  depends_on: string[];
  // Soft edges: ordering only. The job waits for these and reads their outputs,
  // but their failure never skips it - so they do NOT make a job a dependent
  // for tiering or cascade-disable purposes.
  optional_deps: string[];
  // The lanes this stage belongs to. A task's DAG is built from its own lane
  // alone, so a stage in another lane is not part of the graph an analysis of
  // this kind runs at all.
  applies_to: string[];
  // The one stage per lane whose verdict becomes the report.
  verdict_authority: boolean;
  created_at: string;
  updated_at: string;
  served_by: string[];
  effectively_disabled: boolean;
}

// Per-job outcome from a single analysis run's post-analysis DAG.
// Surfaced in the report API response (admin only) so the report page
// can show which jobs succeeded/failed and why.
export interface PAJobRun {
  job: string;
  state: 'succeeded' | 'failed';
  error_reason?: string;
  error_message?: string;
  verdict?: string;
  confidence?: number;
  family?: string;
  duration_ms?: number;
  attempt?: number;
}

export interface PAJobCreate {
  name: string;
  enabled?: boolean;
  timeout_seconds?: number;
  retries?: number;
  depends_on?: string[];
  applies_to?: string[];
  verdict_authority?: boolean;
}

interface PAJobPatch {
  enabled?: boolean;
  timeout_seconds?: number;
  retries?: number;
  depends_on?: string[];
  optional_deps?: string[];
  applies_to?: string[];
  verdict_authority?: boolean;
  config?: unknown;
}

/** The four columns that identify one capacity class, without its bounds. */
export type CapacityPolicyClassKey = Pick<
  CapacityPolicyRow,
  'lane' | 'os_family' | 'base_image_name' | 'analyzer'
>;

export interface ProviderMachineList {
  machines: ProviderMachine[];
  total: number;
}

export interface BaseImage {
  digest: string;
  name: string;
  size_bytes: number;
  uploaded_by: string | null;
  uploaded_at: string | null;
  last_pulled_at: string | null;
  os_family?: OSFamily | null;
  is_orphan?: boolean;
}

/** What GET /api/admin/base-images answers. */
export interface BaseImageList {
  images: BaseImage[];
  /** True when this deployment reads its base images from a read-only source.
   *  Every route that changes one - upload, import, delete, disk bus - answers
   *  409 there, so a page offers none of them. */
  managed_centrally: boolean;
}

export interface Analyzer {
  name: string;
  display_name: string;
  image: string;
  version: string;
  enabled: boolean;
  input_kinds: string[];
  timeout_seconds: number;
  cpus: number;
  memory_mb: number;
  needs_network: boolean;
  /** Which uploaded archive this analyzer loads instead of pulling `image`.
   *  Hex, lowercase, 64 characters, matching AnalyzerImageArchive.sha256.
   *  Null or absent is the ordinary case: `image` is a registry reference.
   *
   *  The same spelling the PATCH that writes it uses, and the same one the
   *  archives list names, so a page can compare them directly. */
  image_archive_sha256?: string | null;
  /** What this deployment tells the analyzer about every run, in the ANALYZER's
   *  own vocabulary. The platform never reads inside it and neither does this
   *  page: it is edited as JSON, because inventing a form for keys nobody here
   *  knows the meaning of would be a form that goes stale the moment an
   *  analyzer grows a setting. Always an object; `{}` means nothing set. */
  options: Record<string, unknown>;
  /** The option KEYS an analyst may set for one run, overriding `options`.
   *  Empty means this analyzer takes nothing per submission, and a submission
   *  that sends any is refused.
   *
   *  Key names only. Which keys may be overridden is a deployment decision;
   *  what they mean is still the analyzer's, and nothing here reads a value. */
  submittable_options: string[];
  created_at: string;
  updated_at: string;
}

/** One uploaded analyzer image archive: a `docker save` tar the deployment
 *  holds in object storage, for hosts with no registry to pull from.
 *
 *  `sha256` is the archive's own digest and the thing an analyzer links to.
 *  `image_digest` is the image inside it, which is what a registry would
 *  report for the same image. They are different facts and neither answers
 *  the other's question. */
// The analyzer-hosts response, re-exported so pages keep importing types from
// here alongside the call that returns them.
export type { WireAnalyzerHost, WireAnalyzerHosts } from './wire-types';

export interface AnalyzerImageArchive {
  sha256: string;
  object_key: string;
  file_size: number;
  image_ref: string;
  image_digest: string;
  platforms: string[];
  uploaded_by: string | null;
  uploaded_at: string;
}

/** The server refuses an analyzer archive over this at presign, and refuses
 *  the multipart path for one at any size, so the browser refuses it before a
 *  long upload can end in a rejection. */
export const ANALYZER_ARCHIVE_MAX_BYTES = 2 * 1024 * 1024 * 1024;

/** One base image the submit page may target, from the member-visible catalog.
 *
 *  `capacity` answers whether a submission is ACCEPTED; `warm_machines` answers
 *  how soon it starts. The two are independent: a fleet can be fully
 *  provisioned and have nothing warm, which costs a boot rather than a
 *  refusal. */
export interface SystemImageChoice {
  name: string;
  os_family: string;
  capacity: boolean;
  machines: number;
  /** How many of `machines` are idle AND warm, so an analysis placed on one
   *  starts in seconds. Moves on its own; zero is ordinary. */
  warm_machines: number;
  /** `machines` split by what each one is doing right now: idle and
   *  dispatchable, or holding an analysis. Provisioning rows are in neither
   *  split. Aggregate counts only - this is the member-visible catalog. */
  ready_machines: number;
  busy_machines: number;
  bare_metal_machines: number;
}

/** One analyzer the submit page may offer, from the member-visible catalog.
 *  `capacity` false means nothing is provisioned to run it, so a submission
 *  naming it would be refused - the page shows that rather than hiding it. */
export interface AnalyzerChoice {
  name: string;
  display_name: string;
  input_kinds: string[];
  capacity: boolean;
  /** The option keys a submission may set for this analyzer. Empty means it
   *  takes none, and the submit form offers nothing. */
  submittable_options: string[];
}

export type {
  AdminUser,
  CreateSSOUserRequest,
  EgressCredential,
  EgressSettings,
  EgressStatus,
  OfferedSSO,
  RoutingStep,
  SCIMConfig,
  SCIMToken,
  SSOConfig,
  SSOEndpoints,
  SSOMode,
  TaskRouting,
} from './api-types.gen';

/** The VPNs an egress credential may belong to. */
export type EgressVPN = 'proton' | 'mullvad' | 'generic';

/** POST /api/admin/egress/credentials. `credential` is write-only: no
 *  response ever carries it back. */
export interface EgressCredentialInput {
  name: string;
  vpn: EgressVPN;
  country?: string;
  credential: string;
  pinned_provider_id?: string;
}

/** PATCH /api/admin/egress/credentials/{id}. An absent field keeps the stored
 *  value, an absent or empty credential keeps the stored credential, and
 *  pinned_provider_id '' clears the pin. */
export interface EgressCredentialPatch {
  name?: string;
  country?: string;
  credential?: string;
  pinned_provider_id?: string;
}

/** GET /api/analyzers as it arrives. Every field is optional because an
 *  orchestrator older than the routing policy omits the two country fields,
 *  and one older than central egress omits egress_configured. */
interface WireAnalyzerCatalog {
  analyzers?: AnalyzerChoice[];
  exit_countries?: string[];
  default_exit_country?: string;
  egress_configured?: boolean;
  vm_exit_countries?: string[];
  default_vm_exit_country?: string;
}

/** The analyzers a submission may name, and the exit countries it may ask for. */
export interface AnalyzerCatalog {
  analyzers: AnalyzerChoice[];
  /** ISO 3166-1 alpha-2 codes a URL submission may name. Empty means the
   *  deployment does not route by country. */
  exitCountries: string[];
  /** The country a submission that names none is routed through. Empty exactly
   *  when `exitCountries` is. */
  defaultExitCountry: string;
  /** False while no egress VPN has passed its live test, which leaves URL
   *  analysis off. */
  egressConfigured: boolean;
  /** The countries a file analysis may name, a separate offer because a
   *  deployment can route URLs while its virtual machines cannot. Empty means
   *  the file submit form offers no picker. */
  vmExitCountries: string[];
  /** Where a file analysis that names no country is routed. Empty exactly
   *  when `vmExitCountries` is. */
  defaultVmExitCountry: string;
}

/**
 * GET /api/analyzers, normalised.
 *
 * An empty `exitCountries` means the deployment does not route by country, and
 * any `exit_country` is refused. An orchestrator older than the policy sends
 * neither field, which reads the same way.
 */
async function readAnalyzerCatalog(): Promise<AnalyzerCatalog> {
  const r = await request<WireAnalyzerCatalog>('/api/analyzers');
  return {
    analyzers: (r.analyzers ?? []).map((a) => ({
      ...a,
      input_kinds: a.input_kinds ?? [],
      submittable_options: a.submittable_options ?? [],
    })),
    exitCountries: r.exit_countries ?? [],
    defaultExitCountry: r.default_exit_country ?? '',
    // An orchestrator older than the field has no egress to be missing.
    egressConfigured: r.egress_configured ?? true,
    vmExitCountries: r.vm_exit_countries ?? [],
    defaultVmExitCountry: r.default_vm_exit_country ?? '',
  };
}

// `options` is omitted rather than made optional: the create endpoint does not
// take it. The column defaults to an empty object and an operator sets it with
// a PATCH afterwards, which keeps registering an analyzer a decision about what
// to RUN rather than one that also asks how to configure it.
export type AnalyzerCreate = Omit<
  Analyzer,
  'created_at' | 'updated_at' | 'options' | 'submittable_options'
>;
/**
 * What POST /api/admin/analyzers actually accepts.
 *
 * `image_archive_sha256` is excluded for the same reason `options` is: the
 * create endpoint has no such field and decodes with unknown fields refused, so
 * a body carrying it is rejected outright. The link is made by the PATCH that
 * follows.
 */
export type AnalyzerCreateBody = Omit<AnalyzerCreate, 'image_archive_sha256'>;
export type AnalyzerPatch = Partial<Omit<AnalyzerCreate, 'name'>> & {
  /** REPLACES the whole object; the server does not merge. Omitting it leaves
   *  the stored options alone, and `{}` clears them. The platform cannot tell
   *  an operator clearing a key from one who did not send it, so a merge would
   *  make a setting impossible to remove. */
  options?: Record<string, unknown>;
  /** REPLACES the whole list, for `options`' reason: withdrawing a key an
   *  analyst may set has to be expressible, and a merge cannot express a
   *  removal. `[]` withdraws every key. */
  submittable_options?: string[];
};

export interface Membership {
  org_id: string;
  user_id: string;
  role: 'member' | 'manager' | 'org_admin';
  org_name: string;
}

/**
 * asClassification maps the wire's verdict vocabulary onto the UI's. The
 * backend speaks the reports vocabulary (clean/suspicious/malicious/
 * inconclusive, with "benign" surviving in older rows); the UI's
 * Classification has no "inconclusive" member, and every tonal surface
 * already fell back to the unknown treatment for it - this makes that mapping
 * deliberate and type-true instead of an unsound cast.
 */
function asClassification(raw: string | undefined): Classification {
  switch (raw) {
    case 'benign':
      return 'clean';
    case 'clean':
    case 'suspicious':
    case 'malicious':
      return raw;
    default:
      return 'unknown';
  }
}

/**
 * assembleReport maps the report wire row into the TaskReport shape ReportPage
 * consumes: a single synthetic "sandbox" plugin carrying the behavior
 * section's structured views, the aggregate verdict, and every section for
 * the pane registry. Returns null while nothing has landed, so callers can
 * fetch this in parallel with the task query without it masquerading as a
 * finished (verdict=unknown) report - except when pa_jobs are present (an
 * admin viewing a post_analysis failure), where a bare shell lets the failure
 * block render per-job outcomes.
 *
 * Exported for tests; the page calls api.getReport.
 */
export function assembleReport(row: WireReportResponse): TaskReport | null {
  const r = row.report;
  const analyses = row.analyses ?? {};
  // The behavior section - the trace extraction the orchestrator serves apart
  // from the ml classifier, for archived reports too - drives the
  // behavior/network/IOC panes, so it is normalised here into the synthetic
  // "sandbox" plugin below. The labels stay the classifier's own, off the ml
  // section. Every other section is handed to its registered pane as-is via
  // jobResults.
  const b = (analyses.behavior ?? {}) as WireBehaviorSection;
  const ml = (analyses.ml ?? {}) as WireMlSection;
  const verdict = asClassification(r?.verdict);
  const score = r != null && typeof r.confidence === 'number' ? Math.round(r.confidence * 100) : 0;

  const indicators: Indicator[] = b.indicators ?? [];
  // Any behaviour-shaped key makes a Behavior: the payload floor keeps
  // ops_by_pid and summary_stats while emptying the walk's lists, and those
  // totals still say what the run was. resolveBehavior normalises the gaps -
  // Partial is the honest wire contract, upheld there once for every consumer.
  const hasBehaviorData = !!(
    b.process_tree ||
    b.processes ||
    b.network ||
    b.ops_by_pid ||
    b.suspicious_calls ||
    b.persistence ||
    b.summary_stats
  );
  const behavior: Behavior | undefined = hasBehaviorData
    ? ({
        process_tree: b.process_tree,
        processes: b.processes,
        network: b.network,
        ops_by_pid: b.ops_by_pid,
        suspicious_calls: b.suspicious_calls,
        persistence: b.persistence,
        summary_stats: b.summary_stats,
      } as Behavior)
    : undefined;

  // The labels clause keeps the plugin alive on a run whose extraction
  // produced nothing but whose classifier still spoke: the inconclusive
  // banner and the archived-family fallback read the labels off the plugin.
  const hasAnalysis =
    behavior !== undefined ||
    !!b.capture ||
    indicators.length > 0 ||
    (ml.labels?.length ?? 0) > 0;

  const paJobs: PAJobRun[] | undefined = row.pa_jobs;
  // Every section, for the pane registry. Sorted by name so tab order is
  // stable across reloads. The envelope keys are absent on the two carved-out
  // sections, which never render through SummaryPane - both are homed.
  const jobResults: JobResult[] = Object.keys(analyses)
    .sort()
    .map((job_name) => {
      const doc = analyses[job_name];
      return {
        job_name,
        schema_version: doc.schema_version,
        impl: doc.impl,
        summary: doc.summary,
        document: doc as Record<string, unknown>,
      };
    });

  if (!r && !hasAnalysis && !paJobs && jobResults.length === 0) return null;

  const plugins: PluginReportView[] = hasAnalysis
    ? [
        {
          plugin_name: 'sandbox',
          report: {
            schema_version: 2,
            plugin: { id: 'sandbox', version: '1' },
            verdict: { classification: verdict, score, labels: ml.labels ?? [] },
            behavior,
            capture: b.capture ?? undefined,
            indicators,
          },
        },
      ]
    : [];

  const report: TaskReport = {
    plugins,
    aggregate: { verdict, indicators },
    // PA job run outcomes (admin-only; absent for regular users).
    pa_jobs: paJobs,
    jobResults,
  };
  if (r?.effective_verdict) {
    report.effectiveVerdict = asClassification(r.effective_verdict);
    report.verdictSource = r.verdict_source ?? undefined;
    report.verdictSourceSubmission = r.verdict_source_submission ?? undefined;
  }
  return report;
}

/** A link into the report, resolved server-side against the panes the report
 *  actually renders - a marker naming anything else is stripped from the answer
 *  and reported nowhere, so every citation that arrives here lands somewhere.
 *
 *  `pane` is the only kind. A `process` kind deep-linked into the Telemetry
 *  tab's rail; that tab is retired and the report keeps no per-process
 *  destination, so the kind went with it rather than pointing somewhere that
 *  ignores the pid. */
export interface AssistCitation {
  kind: 'pane';
  label: string;
  href: string;
}

/** A control the answer offers on one of the run's own dropped files.
 *
 *  The digest is the only part the model chooses. What that file was called,
 *  where it sits in the archive and whether its bytes were kept at all are
 *  facts the report page already holds, so it resolves the offer against its
 *  own drop list and renders nothing for a digest it cannot place.
 *
 *  `submission` is stamped by the service from the conversation's subject, so
 *  the page can refuse an offer that is not about the run on screen. */
export interface AssistAction {
  kind: 'detonate';
  sha256: string;
  submission: string;
}

/** One tool run, as the analyst sees it happen. */
export interface AssistToolEvent {
  name: string;
  state: 'running' | 'ok' | 'failed';
}

/** A completed turn. `concluded` is false when the answer stands on partial
 *  evidence - the budget ran out, or the reply was cut off at the token cap. */
export interface AssistAnswer {
  content: string;
  citations?: AssistCitation[];
  /** Absent from an older service, which is a turn with no offers rather than
   *  a broken one. */
  actions?: AssistAction[];
  concluded?: boolean;
  tools?: string[];
  /** False when the turn could not be written to the transcript. The answer is
   *  still correct; the THREAD is not, because the next follow-up will be
   *  answered from a history missing this exchange. Absent means saved - an
   *  older service that does not send the field has no such failure to report. */
  saved?: boolean;
}

export const api = {

  // ── assistant plane ───────────────────────────────────────────────────
  //
  // The assistant is a separate service behind the same origin at the
  // deployment's gateway path (`/assist` by default), exactly as voltz-logd
  // sits behind `/logs`. The mint response carries that path - the proxy
  // passes it through rather than stripping it, so the string here is the
  // same string the service routes on. The orchestrator
  // mints a short-lived, READ-ONLY delegation the browser then presents to
  // it - sent as an Authorization header the client attaches on purpose,
  // never a cookie, which would ride every unrelated request to that prefix.

  /** Mint a delegation for a conversation about this org's analyses. */
  // (types for this plane are declared beside the /v1 wire types below)
  /**
   * Mint a delegation. Pass `conversationId` to KEEP an existing thread.
   *
   * Without it every mint opens a new conversation, and the assistant refuses a
   * delegation whose conversation is not the one it was minted for - so a
   * renewal partway through a long thread left the page showing history the
   * service could no longer see. The orchestrator validates the id against this
   * org and this user before reusing it.
   */
  assistToken(orgId: string, conversationId?: string, submissionId?: string) {
    const body: { conversation_id?: string; submission_id?: string } = {};
    if (conversationId) body.conversation_id = conversationId;
    // Narrows the delegation to the report's own run. A page served under a
    // token scoped to one submission can mint for that submission only.
    if (submissionId) body.submission_id = submissionId;
    return request<{
      token: string;
      conversation_id: string;
      gateway_path: string;
      expires_at: string;
    }>(`/api/org/${encodeURIComponent(orgId)}/assist/token`, {
      method: 'POST',
      body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * Open a conversation. Created lazily, on the first question.
   *
   * `gatewayPath` comes from the mint response rather than being hardcoded:
   * where the assistant is proxied is a deployment's choice, and the
   * orchestrator is the only side that knows it. A hardcoded prefix works on
   * every default install and 404s on the one that moved it.
   */
  async assistCreateConversation(gatewayPath: string, token: string, submissionId: string) {
    // Its own fetch rather than `request`, because the gateway authenticates on
    // a bearer token instead of the session cookie, but under the same ceiling.
    // It is a one-shot POST, which is exactly the case the ceiling exists for: a
    // gateway that accepts the connection and never answers would leave the
    // assistant opening for the life of the tab.
    return withDeadline(undefined, async (signal) => {
      let resp: Response;
      try {
        resp = await fetch(`${gatewayPath}/conversations`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ submission_id: submissionId }),
          signal,
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!resp.ok) {
        throw new ApiError(resp.status, kindFromStatus(resp.status), 'could not open the conversation');
      }
      try {
        return (await resp.json()) as { id: string; submission_id: string };
      } catch (e) {
        if (isCutShort(e)) throw networkError(e);
        throw e;
      }
    });
  },

  /**
   * Read a conversation's stored transcript, for rehydrating a thread the
   * page no longer holds - a reload, or a report reopened days later.
   *
   * The service filters to the user and agent turns; tool traffic is never
   * persisted. Bounded by the service's own window (the most recent turns),
   * and the rows carry text alone - a rehydrated answer has no citation chips,
   * which is the trade a stored thread makes.
   */
  async assistTranscript(gatewayPath: string, token: string, conversationId: string) {
    return withDeadline(undefined, async (signal) => {
      let resp: Response;
      try {
        resp = await fetch(`${gatewayPath}/conversations/${encodeURIComponent(conversationId)}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!resp.ok) {
        throw new ApiError(resp.status, kindFromStatus(resp.status), 'could not read the conversation');
      }
      try {
        return (await resp.json()) as {
          id: string;
          submission_id: string;
          messages: { seq: number; role: 'user' | 'agent'; content: string; at: string }[];
        };
      } catch (e) {
        if (isCutShort(e)) throw networkError(e);
        throw e;
      }
    });
  },

  /**
   * Ask one question and consume the turn's event stream.
   *
   * fetch + a manual SSE parse rather than EventSource, for one reason:
   * EventSource cannot send an Authorization header or a POST body, and the
   * alternative - the token in the query string - would put a credential in
   * every proxy log and browser history entry it passes through.
   *
   * Outside the request ceiling on purpose, like every other stream in the app.
   * A turn holds its connection open for as long as the answer takes, and a
   * fifteen-minute cap would cut a long one off mid-sentence. What bounds this
   * one instead is the caller's `signal`, which the pane aborts on unmount, so
   * the reader below still has a way to stop.
   */
  async assistAsk(
    gatewayPath: string,
    token: string,
    conversationId: string,
    question: string,
    signal: AbortSignal,
    on: {
      onTool: (t: AssistToolEvent) => void;
      onAnswer: (a: AssistAnswer) => void;
      onError: (message: string) => void;
      /** One fragment of the answer as the model writes it.
       *
       *  Provisional: raw text with no citations bound and no tool names
       *  redacted. `onAnswer` carries the finished, persisted answer and
       *  replaces whatever these built. A caller may omit this and lose only
       *  the progressive render. */
      onAnswerDelta?: (text: string) => void;
      /** Discard every fragment since the last reset — what was streamed was a
       *  preamble to a tool call rather than the start of an answer. */
      onAnswerReset?: () => void;
    },
  ) {
    const resp = await fetch(
      `${gatewayPath}/conversations/${encodeURIComponent(conversationId)}/messages`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
        signal,
      },
    );
    if (!resp.ok || !resp.body) {
      // A refusal's reason is how a reader learns their allowance ran out.
      const message = await reasonOf(resp, 'the assistant could not answer');
      throw new ApiError(resp.status, kindFromStatus(resp.status), message);
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffered = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffered += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line; a partial frame stays in the
      // buffer until its terminator arrives, or a chunk boundary mid-frame
      // would be parsed as a truncated event.
      for (;;) {
        const split = buffered.indexOf('\n\n');
        if (split < 0) break;
        const frame = buffered.slice(0, split);
        buffered = buffered.slice(split + 2);

        let event = 'message';
        let data = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event: ')) event = line.slice(7);
          else if (line.startsWith('data: ')) data = line.slice(6);
        }
        if (!data) continue;

        let payload: unknown;
        try {
          payload = JSON.parse(data);
        } catch {
          continue; // a frame we cannot read is one we skip, not one that ends the turn
        }
        if (!payload || typeof payload !== 'object') continue;
        if (event === 'tool') on.onTool(payload as AssistToolEvent);
        else if (event === 'answer') on.onAnswer(payload as AssistAnswer);
        else if (event === 'answer_delta') {
          const { text } = payload as { text?: string };
          if (text) on.onAnswerDelta?.(text);
        } else if (event === 'answer_reset') on.onAnswerReset?.();
        else if (event === 'error') {
          const { message } = payload as { message?: string };
          on.onError(message ?? 'The assistant could not answer.');
        }
      }
    }
  },
  /**
   * Whether the orchestrator has any admin user yet. The SPA calls this
   * once on boot to decide between the registration page (first-time
   * setup) and the login page. Once any admin exists, this returns
   * `{ needs_bootstrap: false }` for the rest of the system's lifetime.
   */
  bootstrapStatus() {
    return request<{ needs_bootstrap: boolean }>('/api/auth/bootstrap-status');
  },
  /** The single sign-on buttons the sign-in page offers, one per org. */
  ssoOffered() {
    return request<OfferedSSO[]>('/api/auth/sso/offered');
  },
  /** One org's single sign-on, offered on the sign-in page or not. 404 when it has none. */
  ssoOrg(orgId: string) {
    return request<OfferedSSO>(`/api/auth/sso/orgs/${encodeURIComponent(orgId)}`);
  },
  /**
   * Mint a short-lived logs:read credential for the live-logs Console (admin
   * only). The orchestrator drops it as an HttpOnly cookie scoped to the
   * gateway path, so the Console's SSE/query requests authenticate without the
   * browser handling the token at all. The response body carries no token, by
   * design: nothing here reads one, and a cached copy would put a bearer
   * credential in memory for the life of the page. Returns the gateway path to
   * talk to + the expiry so the Console can refresh ahead of time.
   */
  mintLogsToken() {
    return request<{ gateway_path: string; expires_at: string }>('/api/admin/logs/token', {
      method: 'POST',
    });
  },
  /**
   * Create the first admin user + first organization in one shot. The
   * orchestrator validates that no admin exists yet; on success it
   * sets the session cookie so the SPA can land the user on /dashboard
   * without a second round-trip. 409 if setup is already complete.
   */
  bootstrapFirstUser(params: {
    email: string;
    display_name: string;
    password: string;
    org_name: string;
  }) {
    return request<{ user_id: string; org_id: string; email: string }>(
      '/api/auth/bootstrap',
      { method: 'POST', body: JSON.stringify(params) }
    );
  },
  login(email: string, password: string) {
    return request<User>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  logout() {
    return request<{ status: string }>('/api/auth/logout', { method: 'POST' });
  },
  me() {
    return request<User>('/api/auth/me');
  },
  version() {
    return request<{ version: string; frontend_tree: string }>('/api/version');
  },
  resetPassword(token: string, newPassword: string) {
    return request<{ status: string }>('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  },
  changePassword(currentPassword: string, newPassword: string) {
    return request<{ status: string }>('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
  },
  inviteLookup(token: string) {
    return request<InviteLookup>(`/api/invites/lookup?token=${encodeURIComponent(token)}`);
  },
  // Accept an invite. display_name is always sent; password is required only
  // for a brand-new account (empty string for an existing user, which the
  // server ignores - it only adds the membership). A new account gets a
  // session cookie back; an existing account gets existing_account:true and
  // must sign in normally.
  acceptInvite(token: string, displayName: string, password: string) {
    return request<{ user_id: string; email: string; existing_account?: boolean }>(
      '/api/invites/accept',
      {
        method: 'POST',
        body: JSON.stringify({ token, display_name: displayName, password }),
      },
    );
  },
  // --- Submissions ----------------------------------------------------

  /**
   * One page of the org's submissions, newest first, with the counters a
   * pager reads. Without them a reader cannot tell a full page from the end
   * of the list — which is how the dashboard used to show 50 rows as the
   * whole history. `fileHash` is resolved server-side (it matches the
   * submitted sample AND any dropped-file artifact, which client rows do not
   * carry); a caller that passes one gets paged matches for it.
   */
  async listSubmissions(
    orgId: string,
    opts: { fileHash?: string; limit?: number; offset?: number } = {},
  ): Promise<SubmissionPage> {
    const params = new URLSearchParams({ org_id: orgId });
    if (opts.fileHash) params.set('file_hash', opts.fileHash);
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    const page = await request<{
      submissions: SubmissionRow[];
      total: number;
      limit: number;
      offset: number;
      has_more: boolean;
    }>(`/api/submissions?${params.toString()}`);
    return { ...page, submissions: page.submissions.map(normalizeSubmission) };
  },
  async getSubmission(submissionId: string) {
    const row = await request<WireSubmissionDetail>(`/api/submissions/${submissionId}`);
    return normalizeSubmissionDetail(row);
  },
  async getReport(submissionId: string) {
    const row = await request<WireReportResponse>(`/api/submissions/${submissionId}/report`);
    return assembleReport(row);
  },
  cancelSubmission(submissionId: string) {
    return request<{ status: string }>(`/api/submissions/${submissionId}/cancel`, { method: 'POST' });
  },
  /** Delete a submission, its evidence and its report outright. Platform admins
   *  only: the server's admin group refuses anyone else. */
  adminDeleteSubmission(submissionId: string) {
    return request<void>(`/api/admin/submissions/${submissionId}`, { method: 'DELETE' });
  },
  // Distinct from cancel: this ends a RUNNING analysis early and keeps its
  // results, so the submission still produces a report. Cancel discards a run
  // that has nothing worth keeping yet.
  finishSubmission(submissionId: string) {
    return request<{ status: string; task_id: string }>(`/api/submissions/${submissionId}/finish`, { method: 'POST' });
  },
  /**
   * One analyst action, for an analysis that can be driven.
   *
   * Fire and forget by design: the response says the backend took the action,
   * never what the page did with it, and a viewer that waited to find out would
   * stutter on every mouse move. Errors are surfaced by the caller, which knows
   * whether this run is supposed to be driveable at all.
   */
  sendAnalysisInput(submissionId: string, action: AnalysisInput) {
    return request<void>(`/api/submissions/${submissionId}/input`, {
      method: 'POST',
      body: JSON.stringify(action),
    });
  },
  /**
   * A running container analysis's report as its analyzer's backend holds it
   * right now - the document its result will carry, before it has one.
   *
   * The document is the ANALYZER's and is returned unread: the platform does
   * not know its vocabulary, and the console that renders it does. 409 once the
   * run is over, when the stored result is where the same document lives.
   */
  liveReport(submissionId: string) {
    return request<Record<string, unknown>>(`/api/submissions/${submissionId}/live-report`);
  },
  /**
   * Run a running container analysis's AI verdict now - the console's "Finish &
   * analyse". Answers once the pass has run, which takes as long as the model
   * does; `produced` is false when the deployment has the pass switched off or
   * it failed. The verdict itself also arrives on the feed.
   */
  liveVerdict(submissionId: string) {
    return request<{ produced: boolean }>(`/api/submissions/${submissionId}/live-verdict`, {
      method: 'POST',
    });
  },
  /** Where one file a running container analysis's page served downloads from,
   *  as inert text, while the run is still going. */
  liveArtifactUrl(submissionId: string, sha256: string) {
    return `/api/submissions/${submissionId}/live-artifacts/${sha256}`;
  },
  getQuota(orgId: string) {
    return request<UsageView>(`/api/submissions/quota?org_id=${orgId}`);
  },
  /** What the platform will detonate. Static for the life of a deployment. */
  getFileTypes() {
    return request<SupportedFileTypes>('/api/file-types');
  },
  artifactUrl(submissionId: string, resultId: number) {
    return `/api/submissions/${submissionId}/artifacts/${resultId}`;
  },
  artifactZipUrl(submissionId: string) {
    return `/api/submissions/${submissionId}/artifacts.zip`;
  },
  /**
   * One post-analysis stage's dumps archive - the bytes it extracted from the
   * sample. Its own route rather than an entry in the artifact index, which is
   * positional: a second archive's entries would renumber every artifact
   * after them.
   */
  jobDumpsUrl(submissionId: string, jobName: string) {
    return `/api/submissions/${submissionId}/dumps/${encodeURIComponent(jobName)}`;
  },

  /**
   * The submitted document's own bytes, so the report can render its pages.
   *
   * Read into an ArrayBuffer and handed to a worker - never navigated to. The
   * server serves this only for types the preview renders (documents and
   * scripts), so a program's bytes cannot be reached through it, and it
   * answers 404 for everything else including a sample retention has removed.
   */
  async submissionSample(submissionId: string, signal?: AbortSignal): Promise<Uint8Array> {
    // Its own fetch rather than `request`, because the reply is bytes and not
    // JSON, but under the same ceiling. The caller's unmount signal is folded
    // into it rather than replacing it, and `networkError` keeps this call's
    // rule that an abort the caller asked for stays an AbortError.
    return withDeadline(signal, async (deadline) => {
      let resp: Response;
      try {
        resp = await fetch(`/api/submissions/${submissionId}/sample`, {
          credentials: 'include',
          signal: deadline,
        });
      } catch (e) {
        throw networkError(e);
      }
      if (!resp.ok) {
        throw new ApiError(resp.status, kindFromStatus(resp.status), resp.statusText);
      }
      try {
        return new Uint8Array(await resp.arrayBuffer());
      } catch (e) {
        if (isCutShort(e)) throw networkError(e);
        throw e;
      }
    });
  },

  /**
   * The run's result archive, itemized - every downloadable entry with the URL
   * that fetches it.
   *
   * Sourced from the orchestrator's own walk of the archive, NOT from a job
   * document. It used to come out of `analyses.ml.artifacts`, which made the
   * whole evidence list a passenger on the ML stage: that stage contributes no
   * verdict, an operator may disable it, and it is only a soft dependency of
   * `llm` - so a report could carry a full verdict and narrative while offering
   * the reader no files at all, with the archive sitting right there. The index
   * position IS the `{n}` the download endpoint resolves, because both sides
   * read one enumeration server-side.
   */
  async artifactList(submissionId: string) {
    const rows = await requestArray<ReportArtifact>(
      `/api/submissions/${submissionId}/artifacts`,
      'artifacts',
    );
    return rows.map((x, i) => ({
      result_name: x.result_name,
      format: x.format,
      size_bytes: x.size_bytes,
      // Carried as well as spent on the download URL: staging one of these as a
      // sample of its own addresses the same position, and re-deriving it from
      // the URL would be parsing a string this line just built.
      index: i,
      url: `/api/submissions/${submissionId}/artifacts/${i}`,
    }));
  },

  /**
   * Stage one of a run's own artifacts as a new sample.
   *
   * The file a page handed the visitor is evidence about that page and a sample
   * in its own right, and the bytes are already in this deployment - so this is
   * a staging call rather than a download and a re-upload. Answers with the same
   * StagedUpload an ordinary drag-and-drop produces, so the caller continues on
   * the file tree it already knows, and nothing is queued or charged until that
   * flow submits.
   */
  stageRunArtifact(submissionId: string, index: number) {
    return request<StagedUpload>(
      `/api/submissions/${submissionId}/artifacts/${index}/stage`,
      { method: 'POST' },
    );
  },

  /**
   * The submission's precomputed report bundle: every family's totals and
   * COMPLETE fold, written once when the run finalized.
   *
   * The fast path for the FOLDED views. Because it carries the whole fold rather
   * than a page, search and pid filtering run locally over exactly the rows the
   * server would have matched - which is affordable because a fold is bounded by
   * its grouping rather than by the capture.
   *
   * The ONLY source for those views, and for the process rail. A 404 here is a
   * real failure the pane reports, not a slower mode it degrades into.
   */
  eventBundle(submissionId: string) {
    return request<EventBundleResponse>(`/api/submissions/${submissionId}/events/bundle`);
  },

  /**
   * One page of the run's event chronology, oldest first.
   *
   * NOT precomputed, unlike the bundle, and the difference is the point. The
   * chronology used to be flattened into two artifacts at finalization; the
   * larger of them ranked a whole capture through stacked window functions and
   * could not fit the query engine's memory limit, so on any wide capture it
   * silently never built and the Behaviour tab showed hooked syscalls and
   * nothing else. A page is a top-N over a streaming scan - it costs the page,
   * not the capture - which is why per-request is cheaper here than
   * precomputing was, and why nothing in this path is capped or sampled.
   *
   * A 404 means the run kept no derived events at all (the ingest stage was
   * disabled, failed, or predates the artifact). That is permanent, and the
   * pane states it rather than offering a retry that cannot work.
   */
  eventStream(submissionId: string, filter: EventStreamFilter = {}) {
    return request<EventStreamResponse>(
      `/api/submissions/${submissionId}/events/stream${eventStreamQuery(filter)}`,
    );
  },

  /**
   * How many events each source holds under the same filter a page would run
   * with.
   *
   * Separate from the page because it answers a different question: the page
   * shows what the reader selected, this shows what selecting anything else
   * would show. Every source carries a number - including zero, which is a
   * measurement and often a finding in itself.
   */
  eventSources(submissionId: string, filter: EventStreamFilter = {}) {
    return request<EventSourceTotals>(
      `/api/submissions/${submissionId}/events/sources${eventStreamQuery(filter)}`,
    );
  },

  // Multipart upload via XHR (for upload-progress events). The backend
  // reads form fields while iterating parts, so fields MUST be appended
  // before the file part.
  // --- Interactive submission (staged uploads) ------------------------
  //
  // One entry point: stageSubmission uploads and stages in one call. What
  // comes back is a StagedUpload the analyst inspects; getStagedUpload /
  // setStagedPassword / submitStagedUpload are its sub-resources.

  // Stage a sample: upload it, then inspect what is inside before deciding
  // what to detonate. Nothing is queued and no quota is charged until
  // submitStagedUpload is called.
  async stageSubmission(orgId: string, opts: {
    file: File;
    password?: string;
    onProgress?: (loaded: number, total: number) => void;
  }): Promise<StagedUpload> {
    const form = new FormData();
    form.append('interactive', 'true');
    if (opts.password) form.append('password', opts.password);
    // Must precede the file part - the orchestrator reads form fields while
    // streaming the multipart body.
    form.append('target_name', sanitizeTargetName(opts.file.name));
    // No size_hint: the server measures the bytes it receives and no handler
    // ever read the field. A client-declared size is not something intake could
    // trust anyway, so sending one only made the request look like it mattered.
    form.append('file', opts.file);
    return uploadForm<StagedUpload>(`/api/submissions?org_id=${orgId}`, form, opts.onProgress);
  },

  // Read a staged upload and the tree inspection found inside it.
  getStagedUpload(id: string) {
    return request<StagedUpload>(`/api/submissions/staged/${id}`);
  },

  // Supply the password for an encrypted archive. The upload is re-inspected
  // with it; the password is never echoed back.
  setStagedPassword(id: string, password: string) {
    return request<StagedUpload>(`/api/submissions/staged/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },

  // Continue a staged upload: the analysis parameters plus which of the
  // discovered files to detonate. One analysis is queued per entry.
  submitStagedUpload(id: string, opts: {
    entries: string[];
    osFamily?: string;
    baseImageName?: string;
    // `windows` alone: osfamily.SupportedPlatforms derives the accepted set
    // from the families a base image can actually be, and only Windows is one
    // today. Offering `linux` here was a 422 the caller could not see coming.
    platform?: 'windows';
    bareMetal?: boolean;
    timeoutSecs?: number;
    /** Whether these analyses may run on an already-warm machine. Omitted takes
     *  'auto', which is what the server means by leaving the choice to it. */
    startMode?: 'auto' | 'warm' | 'cold';
    /** How the guest should start particular entries, keyed by entry id.
     *
     *  Per entry, unlike everything above it: one upload fans out into one
     *  analysis per entry, and those entries need not be the same kind of file.
     *  An entry left out takes the sandbox's defaults. The server refuses a key
     *  naming an entry this submit does not detonate, and an arm that does not
     *  match the entry's kind, rather than ignoring either. */
    launchOptions?: Record<string, { exe?: { args: string }; dll?: { export?: string; args?: string } }>;
    /** The country every analysis of the batch leaves from. Omitted when the
     *  analyst left the default, so intake applies the default it holds then. */
    exitCountry?: string;
  }) {
    return request<StagedProfileResult>(`/api/submissions/staged/${id}/profile`, {
      method: 'POST',
      body: JSON.stringify({
        entries: opts.entries,
        os_family: opts.osFamily ?? '',
        base_image_name: opts.baseImageName ?? '',
        platform: opts.platform ?? 'windows',
        bare_metal: opts.bareMetal ?? false,
        timeout: opts.timeoutSecs ? String(opts.timeoutSecs) : '',
        start_mode: opts.startMode ?? '',
        ...(opts.exitCountry ? { exit_country: opts.exitCountry } : {}),
        // Omitted when empty rather than sent as {}: the server reads a present
        // object as a deliberate choice and validates its keys against the
        // entries being detonated.
        ...(opts.launchOptions && Object.keys(opts.launchOptions).length > 0
          ? { launch_options: opts.launchOptions }
          : {}),
      }),
    });
  },


  // Stage a sample the analyst only has a URL for: the server fetches the
  // response and stages whatever bytes arrived, answering the same StagedUpload
  // a dropped file gets. Retrieval is the whole contract - the content is
  // inspected like any upload, and nothing here drives a browser at the site;
  // that lane stays on /submit-url. Nothing is queued and no quota is charged
  // until submitStagedUpload is called.
  stageURLSubmission(orgId: string, url: string) {
    return request<StagedUpload>(
      `/api/submissions/staged/url?org_id=${encodeURIComponent(orgId)}`,
      { method: 'POST', body: JSON.stringify({ url }) },
    );
  },

  // --- Threat-intelligence search -------------------------------------
  //
  // The submit page's other half: look an identifier up before deciding
  // whether to run anything. Searching uploads nothing - the term is sent,
  // the file is not.

  /** Look up a hash, URL, IP address or domain.
   *
   *  The server classifies the term; a term it cannot classify comes back 400
   *  with a message naming the shapes it does accept, which is the answer the
   *  box should show rather than a generic failure. */
  intelSearch(orgId: string, q: string) {
    const params = new URLSearchParams({ org_id: orgId, q });
    return request<IntelSearchResponse>(`/api/intel/search?${params.toString()}`);
  },

  /** What the threat-intelligence feed has been seeing lately.
   *
   *  Answered from a deployment-wide cache, so polling it costs nothing
   *  outbound. `skipped` says why the feed went unasked, and an empty chart
   *  with a reason is not the same claim as an empty chart without one. */
  intelActivity(orgId: string) {
    const params = new URLSearchParams({ org_id: orgId });
    return request<IntelActivityResponse>(`/api/intel/activity?${params.toString()}`);
  },

  /** The URL that downloads the file a feed still serves.
   *
   *  A URL rather than a fetch: the browser streams it straight to disk, so a
   *  large archive never lands in the page's memory. What arrives is the
   *  provider's own encrypted archive, never a loose executable - which is
   *  also why it is safe to hand to a browser at all. */
  intelSampleUrl(orgId: string, sha256: string) {
    return `/api/intel/samples/${sha256}?org_id=${encodeURIComponent(orgId)}`;
  },

  /** Fetch the file a feed holds and stage it for analysis.
   *
   *  Answers with an ordinary StagedUpload, so the caller continues on the
   *  same file tree, password prompt and options an uploaded sample gets.
   *  Nothing is queued and no quota is charged until that flow submits. */
  intelStageSample(orgId: string, sha256: string) {
    return request<StagedUpload>(
      `/api/intel/samples/${sha256}/stage?org_id=${encodeURIComponent(orgId)}`,
      { method: 'POST' },
    );
  },

  // --- Admin ----------------------------------------------------------

  listOrgs(opts: { status?: OrgListStatus; q?: string; limit?: number; offset?: number } = {}) {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    const qs = params.toString();
    return request<OrgPage>('/api/admin/orgs' + (qs ? '?' + qs : ''));
  },
  // Single org by id. Returns the bare Org object (404 → ApiError not_found).
  getOrg(orgId: string) {
    return request<Org>(`/api/admin/orgs/${orgId}`);
  },
  createOrg(name: string, notes?: string) {
    return request<Org>('/api/admin/orgs', {
      method: 'POST',
      body: JSON.stringify({ name, notes }),
    });
  },
  updateOrg(orgId: string, fields: { name?: string; notes?: string | null }) {
    return request<Org>(`/api/admin/orgs/${orgId}`, {
      method: 'PATCH',
      body: JSON.stringify(fields),
    });
  },
  archiveOrg(orgId: string) {
    return request<Org>(`/api/admin/orgs/${orgId}/archive`, { method: 'POST' });
  },
  unarchiveOrg(orgId: string) {
    return request<Org>(`/api/admin/orgs/${orgId}/unarchive`, { method: 'POST' });
  },
  // Hard delete. The backend refuses with 409 if the org has any
  // submissions; the UI must surface that as "archive instead".
  deleteOrg(orgId: string) {
    return request<{ status: string }>(`/api/admin/orgs/${orgId}`, { method: 'DELETE' });
  },
  listOrgMembers(orgId: string) {
    return requestArray<OrgMember>(`/api/admin/orgs/${orgId}/members`, 'members');
  },
  addOrgMember(orgId: string, userId: string, role: 'member' | 'manager' | 'org_admin' = 'member') {
    return requestArray<OrgMember>(`/api/admin/orgs/${orgId}/members`, 'members', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, role }),
    });
  },
  removeOrgMember(orgId: string, userId: string) {
    return requestArray<OrgMember>(
      `/api/admin/orgs/${orgId}/members/${userId}`,
      'members',
      { method: 'DELETE' },
    );
  },
  updateOrgQuotas(orgId: string, q: OrgLimitsInput) {
    return request<Org>(`/api/admin/orgs/${orgId}/quotas`, {
      // PATCH, not PUT - the orchestrator route is r.Patch(.../quotas); PUT 405s.
      method: 'PATCH',
      // The patch travels verbatim. A key the caller left undefined does not
      // survive JSON.stringify and reaches the handler absent, which is how a
      // caller says it did not touch that column. `retention_days: null` is not
      // undefined and does survive, which is how it says keep forever.
      body: JSON.stringify(q),
    });
  },
  // Admin (platform) invite. The server derives admin-ness from the absent
  // org_id on the /invites/admin route and takes only the email - display name
  // is collected from the invitee at accept time, and the decoder rejects any
  // unknown field. Returns the copy-link token + accept path.
  sendAdminInvite(email: string) {
    return request<InviteCreated>('/api/admin/invites/admin', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },
  listUsers(opts: { limit?: number; offset?: number; q?: string } = {}) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.offset) params.set('offset', String(opts.offset));
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    return request<{ users: AdminUser[]; total: number; limit: number; offset: number }>(
      '/api/admin/users' + (qs ? '?' + qs : ''),
    );
  },
  setUserActive(userId: string, isActive: boolean) {
    return request<AdminUser>(`/api/admin/users/${userId}/active`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    });
  },
  /** Lets a local account sign in with its password while its orgs enforce SSO. */
  setBreakGlass(userId: string, breakGlass: boolean) {
    return request<AdminUser>(`/api/admin/users/${userId}/break-glass`, {
      method: 'PUT',
      body: JSON.stringify({ break_glass: breakGlass }),
    });
  },
  /** Creates a person who signs in only through an org's identity provider. */
  createSSOUser(body: CreateSSOUserRequest) {
    return request<AdminUser>('/api/admin/users', { method: 'POST', body: JSON.stringify(body) });
  },
  /** The org's SSO config, or null when it has none. */
  async ssoConfig(scope: SSOScope): Promise<SSOConfig | null> {
    try {
      return await request<SSOConfig>(`${ssoBase(scope)}/sso-config`);
    } catch (e) {
      if (e instanceof ApiError && e.kind === 'not_found') return null;
      throw e;
    }
  },
  saveSSOConfig(scope: SSOScope, body: SSOConfigSave) {
    return request<SSOConfig>(`${ssoBase(scope)}/sso-config`, { method: 'PUT', body: JSON.stringify(body) });
  },
  deleteSSOConfig(scope: SSOScope) {
    return request<void>(`${ssoBase(scope)}/sso-config`, { method: 'DELETE' });
  },
  /** What the org's identity provider registers for Voltz, with or without a saved config. */
  ssoEndpoints(scope: SSOScope) {
    return request<SSOEndpoints>(`${ssoBase(scope)}/sso-endpoints`);
  },
  ssoMode(scope: SSOScope) {
    return request<SSOMode>(`${ssoBase(scope)}/sso-mode`);
  },
  setSSOMode(scope: SSOScope, mode: string) {
    const body: SSOMode = { sso_mode: mode };
    return request<SSOMode>(`${ssoBase(scope)}/sso-mode`, { method: 'PUT', body: JSON.stringify(body) });
  },
  scimConfig(scope: SSOScope) {
    return request<SCIMConfig>(`${ssoBase(scope)}/scim`);
  },
  /** Turns SCIM on with a new token, or replaces the token. The token is in this answer only. */
  issueSCIMToken(scope: SSOScope) {
    return request<SCIMToken>(`${ssoBase(scope)}/scim/token`, { method: 'POST' });
  },
  setSCIMAllowlist(scope: SSOScope, allowlist: string[]) {
    const body: SCIMAllowlistRequest = { ip_allowlist: allowlist };
    return request<SCIMConfig>(`${ssoBase(scope)}/scim`, { method: 'PUT', body: JSON.stringify(body) });
  },
  disableSCIM(scope: SSOScope) {
    return request<void>(`${ssoBase(scope)}/scim`, { method: 'DELETE' });
  },
  // Admin-only full fleet view (no tenant filter).
  listMachines() {
    return requestArray<Machine>('/api/admin/machines', 'machines');
  },
  getMachine(id: string) {
    return request<Machine>(`/api/admin/machines/${id}`);
  },
  listMachineSnapshots(id: string) {
    return requestArray<MachineSnapshot>(`/api/admin/machines/${id}/snapshots`, 'snapshots');
  },
  listMachineProvisions(id: string) {
    return requestArray<ProvisionRun>(`/api/admin/machines/${id}/provisions`, 'provisions');
  },

  // --- Machine actions (admin) ----------------------------------------
  // The two exits a machine has. Both are keyed on the machine id alone - it is
  // unique fleet-wide, so the orchestrator resolves the owning provider itself.
  deleteMachine(id: string) {
    return request<void>(`/api/admin/machines/${id}`, { method: 'DELETE' });
  },
  // Destroy the VM and build it again from the same spec. Only a quarantined
  // machine can be rebuilt; anything else returns 409.
  reprovisionMachine(id: string) {
    return request<void>(`/api/admin/machines/${id}/reprovision`, { method: 'POST' });
  },
  fetchOrgStats(orgId: string) {
    return request<OrgStats>(`/api/admin/orgs/${orgId}/stats`);
  },
  listOrgAuditEvents(orgId: string, limit = 100) {
    return requestArray<AuditEvent>(`/api/admin/orgs/${orgId}/audit?limit=${limit}`, 'events');
  },
  // --- Orchestration (replaces the old runtime config page) ---
  // Returns the canonical hypervisor registry plus managed_available -
  // whether this deployment can provision orchestrator-managed providers.
  listHypervisors() {
    return request<{
      hypervisors: import('./providers').HypervisorDef[];
      managed_available: boolean;
    }>('/api/admin/orchestration/hypervisors');
  },
  listOrchestrationProviders() {
    return requestArray<OrchestrationProvider>('/api/admin/orchestration/providers', 'providers');
  },
  // Returns the full row including the decrypted config blob (ListProviders
  // strips it). The edit modal calls this on open to populate its form.
  getOrchestrationProvider(id: string) {
    return request<OrchestrationProvider>(`/api/admin/orchestration/providers/${id}`);
  },
  // Mints a pending provider row + one-time claim code (mirrors
  // generateProviderClaimCode's paworker counterpart, createPaworker).
  // The provider-installer redeems the code by dialing OUT to the
  // orchestrator's unified Enrollment.Enroll RPC on :8447 - the
  // orchestrator never dials the provider to enroll it. Hypervisor type,
  // host capacity, and (for remote backends) hypervisor connection config
  // are all self-reported by the provider agent at enroll time, not
  // collected here. With managed=true the orchestrator provisions the
  // provider on its own Docker host: the response omits the claim code
  // (it is redeemed in-process) and the row appears as pending until the
  // managed enroll lands.
  generateProviderClaimCode(body: {
    name: string;
    managed?: boolean;
    hypervisor?: string;
  }) {
    return request<ProviderClaimCode>('/api/admin/orchestration/providers/claim-code', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  // Re-issues a claim code for a provider that already exists, so a host
  // whose 90-day leaf expired can re-enroll under the same id. Renewal is
  // served over the connection that leaf authenticates, so past its NotAfter
  // this is the only way back - and a new provider row would strand the
  // machines the old one still owns. The operator runs the returned command
  // with --re-enroll. Refused (409) for managed providers, whose code is
  // redeemed in-process and never reaches a human.
  regenerateProviderClaim(id: string) {
    return request<ProviderClaimCode>(
      `/api/admin/orchestration/providers/${id}/regenerate-claim`,
      { method: 'POST' },
    );
  },
  // What one select field of the connection config may be set to, listed by
  // the provider's backend for the draft config the dialog holds. POST because
  // the draft carries credentials. 409 means the draft is missing a field the
  // listing needs (not yet), 404 that this provider lists nothing.
  listProviderConfigOptions(id: string, source: string, draft: Record<string, unknown>) {
    return request<ConfigOptionsResponse>(
      `/api/admin/orchestration/providers/${id}/config-options/${source}`,
      { method: 'POST', body: JSON.stringify(draft) },
    );
  },
  updateOrchestrationProvider(id: string, body: OrchestrationProviderPatch) {
    return request<OrchestrationProviderSaveResult>(`/api/admin/orchestration/providers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  deleteOrchestrationProvider(id: string, drain = false) {
    return request<void>(
      `/api/admin/orchestration/providers/${id}${drain ? '?drain=true' : ''}`,
      { method: 'DELETE' }
    );
  },
  reorderOrchestrationProviders(ids: string[]) {
    return requestArray<OrchestrationProvider>('/api/admin/orchestration/reorder', 'providers', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },
  testOrchestrationProvider(id: string) {
    return request<{
      ok: boolean;
      name: string;
      hypervisor?: string;
      in_flight: number | null;
      capabilities: string[];
      error?: string;
    }>(`/api/admin/orchestration/providers/${id}/test`, { method: 'POST' });
  },
  // Restart a managed provider's container. 404 on self-managed providers.
  restartManagedProvider(id: string) {
    return request<{ status: string }>(`/api/admin/orchestration/providers/${id}/restart`, {
      method: 'POST',
    });
  },
  // Fleet capacity + per-org utilization snapshot for the all-orgs
  // (platform console) dashboard view. Admin-only route.
  getOrchestrationInflight() {
    return request<InflightResponse>('/api/admin/orchestration/inflight');
  },

  // Resolves a task id back to its submission id so admin
  // pages can deep-link from "current task" to the report page.
  resolveTaskSubmission(taskId: string) {
    return request<{ task_id: string; submission_id: string; status: string }>(
      `/api/admin/tasks/${taskId}/submission`,
    );
  },

  // --- System (Chunk A) -----------------------------------------------
  // Kick a backup. 202 the moment pg_dump is launched, NOT when it finishes -
  // the run is detached so a closed browser tab cannot kill it. The dump's
  // outcome comes from backupRun below.
  runBackup() {
    return request<{ run_id: string; status: string }>(
      '/api/admin/backup/run',
      { method: 'POST' },
    );
  },
  // One run's state. `running` false is terminal: `error` when it failed,
  // otherwise `location` names the dump - a path on a local target, an object
  // key on a remote one.
  backupRun(id: string) {
    return request<BackupRun>(`/api/admin/backup/runs/${encodeURIComponent(id)}`);
  },

  // Post-install setup wizard status + dismiss.
  setupStatus() {
    return request<SetupStatus>('/api/admin/setup/status');
  },
  dismissSetup() {
    return request<{ status: string }>('/api/admin/setup/dismiss', { method: 'POST' });
  },

  // Admin-only: set the per-provider default vCPU + RAM. Path stays /bounds
  // to avoid an API rename; body is just the two default_* fields.
  updateProviderDefaults(providerId: string, defaults: { default_cpus: number; default_memory_mb: number }) {
    return request<{ status: string }>(
      `/api/admin/orchestration/providers/${providerId}/bounds`,
      { method: 'PUT', body: JSON.stringify(defaults) },
    );
  },

  getProviderPaworkers(providerId: string) {
    return request<ProviderPaworkers>(`/api/admin/orchestration/providers/${providerId}/paworkers`)
  },
  // The shape is sent only when the operator picks a size: a count-only save
  // sends the count alone, and the orchestrator keeps the stored shape.
  setProviderPaworkers(providerId: string, body: { count: number, cpus?: number, memory_mb?: number }) {
    return request<SetProviderPaworkersResponse>(
      `/api/admin/orchestration/providers/${providerId}/paworkers`,
      { method: 'PUT', body: JSON.stringify(body) },
    )
  },
  // Admin-only: set how much of a local provider's host Voltz may commit to
  // machines. This is the ceiling the provisioning guard enforces, which is why
  // it is separate from updateProviderDefaults above: that one sizes a single
  // VM, this one caps the total.
  updateProviderCapacity(
    providerId: string,
    budget: { budget_cpus: number; budget_memory_mb: number },
  ) {
    return request<{ status: string }>(
      `/api/admin/orchestration/providers/${providerId}/capacity`,
      { method: 'PUT', body: JSON.stringify(budget) },
    );
  },

  // --- Capacity policy -------------------------------------------------
  // What the autoscaler holds a provider at, per affinity class. A provider
  // with at least one class is sized by the loop, and provisionProviderMachines
  // below refuses for it: there is exactly one writer of that capacity.
  getCapacityPolicy(providerId: string) {
    return request<CapacityPolicyList>(
      `/api/admin/orchestration/providers/${providerId}/capacity-policy`,
    );
  },

  // Writes one class's floor and ceiling, creating the class if it is new.
  // Deliberately not a whole-policy replace: editing one class must not be
  // able to delete another by omitting it from a payload built off a stale
  // read.
  putCapacityPolicy(providerId: string, body: CapacityPolicyEdit) {
    return request<CapacityPolicyList>(
      `/api/admin/orchestration/providers/${providerId}/capacity-policy`,
      { method: 'PUT', body: JSON.stringify(body) },
    );
  },

  // Removes a class. The machines it already has are left alone - the policy
  // going away hands that capacity back rather than destroying it.
  deleteCapacityPolicy(providerId: string, c: CapacityPolicyClassKey) {
    const q = new URLSearchParams({
      lane: c.lane,
      os_family: c.os_family,
      base_image_name: c.base_image_name,
      analyzer: c.analyzer,
    });
    return request<CapacityPolicyList>(
      `/api/admin/orchestration/providers/${providerId}/capacity-policy?${q}`,
      { method: 'DELETE' },
    );
  },

  // Clears a suspension so the loop may create for the class again. 409 when
  // the class was not suspended, which is the difference between "cleared" and
  // "there was nothing to clear".
  resumeCapacityClass(providerId: string, c: CapacityPolicyClassKey) {
    return request<CapacityPolicyList>(
      `/api/admin/orchestration/providers/${providerId}/capacity-policy/resume`,
      { method: 'POST', body: JSON.stringify(c) },
    );
  },

  // --- Per-provider machine management --------------------------------
  // The list call auto-imports provider-known VMs into the daemon's DB, so
  // opening the detail page is enough to make the scheduler dispatch to them.
  listProviderMachines(providerId: string) {
    return request<ProviderMachineList>(`/api/admin/orchestration/providers/${providerId}/machines`);
  },

  /**
   * The provisioning choices this provider can honour: how machines are sized,
   * which machine types its setup prepared, whether it can do bare metal, and
   * which base images it can actually boot.
   */
  getProvisioningOptions(providerId: string) {
    return request<ProvisioningOptions>(
      `/api/admin/orchestration/providers/${providerId}/provisioning-options`,
    );
  },

  provisionProviderMachines(
    providerId: string,
    body: {
      count: number;
      profile: string;
      name_prefix?: string;
      // Free-form sizing only (libvirt). Per-batch override, also written back
      // as the provider default so the form pre-fills it next time. Rejected by
      // profile-sized backends, which never receive a size.
      cpus?: number;
      memory_mb?: number;
      // Profile sizing only: the backend machine type to create. Optional when
      // the provider is set up for exactly one.
      machine_type?: string;
      // Execution lane. Omit for VMs; only accepted when the hypervisor can
      // actually provision bare metal.
      bare_metal?: boolean;
      // Naming an analyzer makes the batch CONTAINER RUNNERS instead of virtual
      // machines, and every field above is then ignored: there is no image to
      // resolve and the analyzer row carries the size.
      analyzer?: string;
    },
  ) {
    return request<{ created: string[]; errors: string[] }>(
      `/api/admin/orchestration/providers/${providerId}/machines`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },

  // --- Post-analysis workers ------------------------------------------
  // Mirrors the provider CRUD surface (list/reorder/get/create/update/
  // delete/test), plus drain for pre-delete safe-shutdown.
  listPaworkers() {
    return requestArray<PostAnalysisWorker>('/api/admin/paworkers', 'paworkers');
  },
  getPaworker(id: string) {
    return request<PostAnalysisWorker>(`/api/admin/paworkers/${id}`);
  },
  createPaworker(body: PostAnalysisWorkerCreate) {
    return request<PostAnalysisWorkerCreated>('/api/admin/paworkers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  regeneratePaworkerClaim(id: string) {
    return request<PostAnalysisWorkerCreated>(
      `/api/admin/paworkers/${id}/regenerate-claim`,
      { method: 'POST' },
    );
  },
  updatePaworker(id: string, body: PostAnalysisWorkerPatch) {
    return request<PostAnalysisWorker>(`/api/admin/paworkers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  deletePaworker(id: string) {
    return request<void>(`/api/admin/paworkers/${id}`, { method: 'DELETE' });
  },
  reorderPaworkers(ids: string[]) {
    return requestArray<PostAnalysisWorker>('/api/admin/paworkers/reorder', 'paworkers', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  },
  testPaworker(id: string) {
    return request<{
      ok: boolean;
      name: string;
      capabilities: string[];
      in_flight?: Record<string, number>;
      host_labels?: string[];
      error?: string;
    }>(`/api/admin/paworkers/${id}/test`, { method: 'POST' });
  },
  drainPaworker(id: string, graceSeconds?: number) {
    const qs = graceSeconds ? `?grace=${graceSeconds}` : '';
    return request<{ status: string; grace_seconds: number }>(
      `/api/admin/paworkers/${id}/drain${qs}`,
      { method: 'POST' },
    );
  },

  // --- Post-analysis DAG -------------------------------------------------
  // Operator-editable post-analysis jobs. Mutations re-tickle the scheduler
  // so changes apply on the next dispatch, not after the 30s reload tick.
  async listPAJobs() {
    const rows = await requestArray<PAJob>(
      '/api/admin/post-analysis/jobs',
      'jobs',
    );
    // Go marshals nil slices as JSON null, but the DAG editor reads
    // depends_on.length / served_by.length during render - a tier-0 or
    // unserved step would crash the page. Normalise to [] at the boundary.
    return rows.map((s) => ({
      ...s,
      depends_on: s.depends_on ?? [],
      optional_deps: s.optional_deps ?? [],
      // An unstated lane reads as the virtual-machine lane, matching the
      // column's own default, so a row written before lanes existed renders
      // where it has always belonged.
      applies_to: s.applies_to?.length ? s.applies_to : [VM_LANE],
      served_by: s.served_by ?? [],
    }));
  },
  createPAJob(body: PAJobCreate) {
    return request<PAJob>('/api/admin/post-analysis/jobs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updatePAJob(name: string, body: PAJobPatch) {
    return request<PAJob>(
      `/api/admin/post-analysis/jobs/${encodeURIComponent(name)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    );
  },
  deletePAJob(name: string) {
    return request<{ status: string }>(
      `/api/admin/post-analysis/jobs/${encodeURIComponent(name)}`,
      { method: 'DELETE' },
    );
  },
  async listAvailableJobTypes(): Promise<string[]> {
    const r = await request<{ available: string[] }>('/api/admin/post-analysis/jobs/available');
    return r.available ?? [];
  },
  // How long post-analysis actually takes here: per job the p50, p95 and max
  // of the succeeded runs over the window, and the same for report bundle
  // builds. The jobs page shows the per-job figures beside each timeout.
  async getPostAnalysisStats(days = 30): Promise<PostAnalysisStats> {
    const r = await request<PostAnalysisStats>(`/api/admin/post-analysis/stats?days=${days}`);
    return { ...r, jobs: r.jobs ?? [] };
  },

  // Which analyzers a submission may name. Member-visible, unlike the admin
  // registry below: choosing what to analyse is not an operator action.
  async listAnalyzerChoices(): Promise<AnalyzerChoice[]> {
    return (await readAnalyzerCatalog()).analyzers;
  },

  /**
   * The analyzer list plus the deployment's exit-country routing policy, which
   * the server publishes on the same read because the URL console needs both
   * before its first submission.
   */
  analyzerCatalog(): Promise<AnalyzerCatalog> {
    return readAnalyzerCatalog();
  },

  // --- Egress (/api/admin/egress) ------------------------------------
  // The VPN credentials URL analysis leaves through. The credential text goes
  // up and never comes back: every read omits it.
  listEgressCredentials() {
    return requestArray<EgressCredential>('/api/admin/egress/credentials', 'credentials');
  },
  createEgressCredential(input: EgressCredentialInput) {
    return request<EgressCredential>('/api/admin/egress/credentials', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  updateEgressCredential(id: string, patch: EgressCredentialPatch) {
    return request<EgressCredential>(`/api/admin/egress/credentials/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  deleteEgressCredential(id: string) {
    return request<void>(`/api/admin/egress/credentials/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  getEgressSettings() {
    return request<EgressSettings>('/api/admin/egress/settings');
  },
  putEgressSettings(s: EgressSettings) {
    return request<EgressSettings>('/api/admin/egress/settings', {
      method: 'PUT',
      body: JSON.stringify(s),
    });
  },
  getEgressStatus() {
    return request<EgressStatus>('/api/admin/egress/status');
  },

  // The file lane's twin of the call above: which base images a submission may
  // target, and how much of the fleet behind each can start right now.
  async listSystemImages() {
    return requestArray<SystemImageChoice>('/api/systems', 'images');
  },
  /**
   * What has this org already run on exactly this link?
   *
   * The URL lane's half of what the staged file tree answers per entry, and of
   * the "Analysed here" card the identifier search renders for a digest. A URL
   * run costs a container, a live browser and an analyst's attention, and an
   * analyst pasting a link out of a ticket has no way to know it was run an
   * hour ago — or three times last week, which is what a list says and a
   * single newest run does not.
   *
   * EXACT match, because intake does not normalise a submitted URL: a fragment
   * routes a single-page application and userinfo is often the technique under
   * analysis, so a looser match would answer about a different page. An empty
   * list means never analysed; the call throwing means we could not tell,
   * which is a different answer and must not render as the first.
   */
  async priorURLAnalyses(orgId: string, url: string) {
    const q = `org_id=${encodeURIComponent(orgId)}&url=${encodeURIComponent(url)}`;
    return requestArray<IntelLocalAnalysis>(`/api/submissions/prior-url?${q}`, 'runs');
  },

  /**
   * Has this org already analysed a file with these exact bytes?
   *
   * The file lane's twin of priorURLAnalyses, and keyed on the DIGEST alone —
   * which is the point. An executable resubmitted under another name is the
   * same file, and a check that looked at the name would report it as new.
   *
   * The staged tree answers the same question per entry, but only after the
   * whole file has been uploaded and inspected. The dashboard hashes in a
   * worker before the upload starts, so asking here tells an analyst they
   * already hold this report without spending the transfer.
   */
  async priorSampleAnalyses(orgId: string, sha256: string) {
    const q = `org_id=${encodeURIComponent(orgId)}&sha256=${encodeURIComponent(sha256)}`;
    return requestArray<IntelLocalAnalysis>(`/api/submissions/prior-sample?${q}`, 'runs');
  },

  /**
   * Stage a sample this deployment already holds, so it can be run again.
   *
   * No bytes move: a prior run put them in this org's storage, and the digest
   * is how they are addressed. Answers with the same StagedUpload a
   * drag-and-drop produces, so the caller continues on the file tree it knows.
   */
  restageSample(orgId: string, sha256: string) {
    const q = `org_id=${encodeURIComponent(orgId)}`;
    return request<StagedUpload>(`/api/samples/${encodeURIComponent(sha256)}/stage?${q}`, {
      method: 'POST',
    });
  },

  /**
   * The other runs of a submission's target.
   *
   * Keyed on what the submission IS — a digest, or a URL byte for byte — so a
   * re-run is linked to what it re-ran rather than to when it happened. The
   * submission itself is not in the answer: its own report is the page this is
   * read from.
   */
  async submissionHistory(submissionId: string) {
    return requestArray<IntelLocalAnalysis>(`/api/submissions/${submissionId}/history`, 'runs');
  },

  // Submit a target that has no bytes. JSON rather than multipart, which is
  // what the server dispatches on.
  submitURL(body: {
    org_id: string;
    url: string;
    analyzer: string;
    timeout?: number;
    /** What this run should do differently, in the ANALYZER's vocabulary.
     *  Omitted when the analyst changed nothing - an empty object would be a
     *  claim they chose something. Keys outside the analyzer's declared
     *  `submittable_options` are refused rather than dropped. */
    analyzer_options?: Record<string, unknown>;
    /** The country the run's traffic to the target leaves from, one of the
     *  catalog's `exitCountries`. A field of the submission, never an analyzer
     *  option. Omitted when the analyst chose none, so intake applies the
     *  deployment default: an empty string would be refused as malformed. */
    exit_country?: string;
  }) {
    return request<{ submission_id: string; target_url: string; analyzer: string }>(
      '/api/submissions',
      {
        // request() already labels a non-FormData body as JSON, which is
        // what the server dispatches the target shape on.
        method: 'POST',
        body: JSON.stringify({
          org_id: body.org_id,
          target: { kind: 'url', url: body.url },
          analyzer: body.analyzer,
          ...(body.timeout ? { timeout: body.timeout } : {}),
          ...(body.analyzer_options && Object.keys(body.analyzer_options).length > 0
            ? { analyzer_options: body.analyzer_options }
            : {}),
          ...(body.exit_country ? { exit_country: body.exit_country } : {}),
        }),
      },
    );
  },

  // --- Analyzers --------------------------------------------------------
  // The registry of analysis containers. Empty on a fresh deployment: there
  // are no seed rows, because an image nothing has built cannot be pulled.
  async listAnalyzers() {
    const rows = await requestArray<Analyzer>('/api/admin/analyzers', 'analyzers');
    // Go marshals a nil slice as null and the page reads input_kinds.length
    // while rendering. Normalise at the boundary, as listPAJobs does.
    return rows.map((a) => ({ ...a, input_kinds: a.input_kinds ?? [] }));
  },
  // Every analyzer image archive this deployment holds, newest first.
  async listAnalyzerArchives() {
    const rows = await requestArray<AnalyzerImageArchive>(
      '/api/admin/analyzer-archives',
      'archives',
    );
    return rows.map((a) => ({ ...a, platforms: a.platforms ?? [] }));
  },
  // Remove one archive: the row and the stored object. Refused with 409 while
  // an analyzer still links it, and the message names the analyzers to
  // repoint. `stranded_object_key` is present only when the record went and
  // the bytes did not, which is the one outcome the operator has to act on.
  deleteAnalyzerArchive(sha256: string) {
    return request<{ status: string; stranded_object_key?: string }>(
      `/api/admin/analyzer-archives/${encodeURIComponent(sha256)}`,
      { method: 'DELETE' },
    );
  },
  // Which provider hosts last reported holding this analyzer's image, and
  // whether the standing backend it names is up on each. 404 only for an
  // analyzer the registry does not carry: one nothing has reported is a 200
  // with an empty list, because those are different facts.
  getAnalyzerHosts(name: string) {
    return request<WireAnalyzerHosts>(
      `/api/admin/analyzers/${encodeURIComponent(name)}/hosts`,
    );
  },
  createAnalyzer(body: AnalyzerCreateBody) {
    return request<Analyzer>('/api/admin/analyzers', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateAnalyzer(name: string, body: AnalyzerPatch) {
    return request<Analyzer>(`/api/admin/analyzers/${encodeURIComponent(name)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },
  deleteAnalyzer(name: string) {
    return request<{ status: string }>(`/api/admin/analyzers/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    });
  },

  // --- Base VM images ---------------------------------------------------
  // Content-addressed (sha256) store. Provider agents pull canonical
  // bytes from /v1/images/{digest}/blob; the admin UI uploads here.
  // --- Hypervisor setup -----------------------------------------------
  // Task 5 endpoints: start/poll/abort the image-preparation run for cloud
  // providers (Hetzner) that need the base image uploaded to their registry
  // before VMs can be provisioned.
  startHypervisorSetup(
    providerId: string,
    body: { image_shas: string[]; approved_gap_codes: string[] },
  ) {
    return request<HypervisorSetupStatusResponse>(
      `/api/admin/orchestration/providers/${providerId}/setup`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  },
  // What the run would create or change, read before starting it. The answer
  // depends on which images are selected, so the caller keys its query on the
  // same list it is about to send.
  getHypervisorSetupPreflight(providerId: string, imageShas: string[]) {
    const q = imageShas.length ? `?image_shas=${imageShas.join(',')}` : '';
    return request<SetupPreflightResponse>(
      `/api/admin/orchestration/providers/${providerId}/setup/preflight${q}`,
    );
  },
  getHypervisorSetupStatus(providerId: string) {
    return request<HypervisorSetupStatusResponse>(
      `/api/admin/orchestration/providers/${providerId}/setup`,
    );
  },
  abortHypervisorSetup(providerId: string) {
    return request<{ status: string }>(
      `/api/admin/orchestration/providers/${providerId}/setup/abort`,
      { method: 'POST' },
    );
  },

  async listBaseImages(): Promise<BaseImageList> {
    const r = await request<{ images: BaseImage[]; managed_centrally?: boolean }>(
      '/api/admin/base-images',
    );
    // An orchestrator older than the field has no read-only image source, so
    // absent means the images are changeable.
    return { images: r.images, managed_centrally: r.managed_centrally === true };
  },
  deleteBaseImage(digest: string) {
    return request<void>(`/api/admin/base-images/${digest}`, { method: 'DELETE' });
  },
  // Adopts an orphan image (object in storage, DB row is_orphan=true) by
  // stamping an operator-supplied name + OS family so it's usable.
  importBaseImage(body: {
    sha256: string;
    name: string;
    os_family: OSFamily;
  }) {
    return request<{ sha256: string }>(
      '/api/admin/images/import',
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
  },
  // Re-walks object storage for images that have no database row. The
  // orchestrator otherwise only scans at startup, so an image uploaded to the
  // bucket out of band stays invisible until it restarts. Returns how many new
  // orphans the scan adopted.
  rescanBaseImages() {
    return request<{ discovered: number }>('/api/admin/images/rescan', {
      method: 'POST',
    });
  },
  // Agent-package CRUD removed: agent.exe + KernelAgent.* are now baked
  // into the base image (SPEC §12.2 image_repack), updated via rebuild.
  // uploadBaseImage removed too - uploads flow through the global
  // UploadProvider so they survive navigation (see context/UploadContext.tsx).

  // --- API keys -------------------------------------------------------
  listAPIKeys(orgID: string) {
    return requestArray<OrgAPIKey>(`/api/admin/orgs/${orgID}/api-keys`, 'api_keys');
  },
  createAPIKey(orgID: string, name: string, scopes?: string[]) {
    return request<OrgAPIKey & { secret: string }>(`/api/admin/orgs/${orgID}/api-keys`, {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    });
  },
  revokeAPIKey(orgID: string, keyID: string) {
    return request<void>(`/api/admin/orgs/${orgID}/api-keys/${keyID}`, { method: 'DELETE' });
  },

  // --- Platform keys (/api/admin/platform-keys) ---------------------
  // The operator credential for the /v1 fleet and org-lifecycle surface. It
  // belongs to the deployment rather than to an org, which is why it is minted
  // here and not on an org's page. Minting stays behind this admin session on
  // purpose: a key that could mint keys would be a privilege ladder with no
  // rung that needs a person.
  listPlatformKeys() {
    return requestArray<PlatformKey>('/api/admin/platform-keys', 'platform_keys');
  },
  createPlatformKey(name: string, scopes: string[]) {
    return request<PlatformKey & { secret: string }>('/api/admin/platform-keys', {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    });
  },
  revokePlatformKey(keyID: string) {
    return request<void>(`/api/admin/platform-keys/${keyID}`, { method: 'DELETE' });
  },

  // --- Org self-service portal (/api/org/{org_id}/*) ----------------
  // Org-scoped surface for customers without a platform-admin account.
  // Gated on org_memberships.role: member sees, manager+ writes.
  orgListAPIKeys(orgID: string) {
    return requestArray<OrgAPIKey>(`/api/org/${orgID}/api-keys`, 'api_keys');
  },
  orgCreateAPIKey(orgID: string, name: string, scopes?: string[]) {
    return request<OrgAPIKey & { secret: string }>(`/api/org/${orgID}/api-keys`, {
      method: 'POST',
      body: JSON.stringify({ name, scopes }),
    });
  },
  orgRevokeAPIKey(orgID: string, keyID: string) {
    return request<void>(`/api/org/${orgID}/api-keys/${keyID}`, { method: 'DELETE' });
  },
  orgGetUsage(orgID: string) {
    return request<UsageView>(`/api/org/${orgID}/usage`);
  },
  orgListMembers(orgID: string) {
    return requestArray<OrgMember>(`/api/org/${orgID}/members`, 'members');
  },
  // Org-portal writes accept only 'member'/'manager'; the server normalizes
  // legacy 'org_admin' rows to 'manager', so clients never send it.
  orgInvite(orgID: string, email: string, role: 'member' | 'manager' = 'member') {
    return request<{ id: string; email: string; role: string; token: string; accept_path: string }>(
      `/api/org/${orgID}/invites`,
      { method: 'POST', body: JSON.stringify({ email, role }) },
    );
  },
  orgChangeMemberRole(orgID: string, userID: string, role: 'member' | 'manager') {
    return request<void>(`/api/org/${orgID}/members/${userID}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  },
  orgRemoveMember(orgID: string, userID: string) {
    return request<void>(`/api/org/${orgID}/members/${userID}`, { method: 'DELETE' });
  },
  orgRename(orgID: string, name: string) {
    return request<{ id: string; name: string }>(`/api/org/${orgID}`, {
      method: 'PATCH',
      body: JSON.stringify({ name }),
    });
  },
  /**
   * The organization's report logo as bytes, or null when none is set.
   *
   * Its own fetch, like `submissionSample`: the reply is an image rather than
   * JSON. A 404 is the ordinary answer for an organization that has never
   * uploaded one, so it resolves to null rather than throwing - the caller's
   * question is "is there a logo", not "did the request fail".
   */
  async orgLogo(orgID: string, signal?: AbortSignal): Promise<Blob | null> {
    return withDeadline(signal, async (deadline) => {
      let resp: Response;
      try {
        resp = await fetch(`/api/org/${orgID}/logo`, {
          credentials: 'include',
          signal: deadline,
        });
      } catch (e) {
        throw networkError(e);
      }
      if (resp.status === 404) return null;
      if (!resp.ok) {
        throw new ApiError(resp.status, kindFromStatus(resp.status), resp.statusText);
      }
      try {
        return await resp.blob();
      } catch (e) {
        if (isCutShort(e)) throw networkError(e);
        throw e;
      }
    });
  },
  /** Replaces the organization's report logo. PNG or JPEG, up to 1 MiB; the
   *  server validates the bytes and answers 415/413/400 with a reason. */
  orgSetLogo(orgID: string, file: File) {
    return request<{ content_type: string; size_bytes: number }>(`/api/org/${orgID}/logo`, {
      method: 'PUT',
      body: file,
      // The whole header set, because `request` replaces its defaults with a
      // caller's headers rather than merging them - and the JSON default is
      // wrong for a raw image body anyway.
      headers: { Accept: 'application/json', 'Content-Type': file.type },
    });
  },
  orgDeleteLogo(orgID: string) {
    return request<void>(`/api/org/${orgID}/logo`, { method: 'DELETE' });
  },
  listAuditEvents(limit = 200) {
    return requestArray<AuditEvent>(`/api/admin/audit?limit=${limit}`, 'events');
  },

  // Notifications - per-user bell feed.
  listNotifications(opts: { limit?: number; unreadOnly?: boolean } = {}) {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.unreadOnly) params.set('unread', 'true');
    const qs = params.toString();
    return request<{ notifications: Notification[]; unread: number }>(
      '/api/notifications' + (qs ? '?' + qs : ''),
    );
  },
  markNotificationRead(id: string) {
    return request<{ status: string }>(`/api/notifications/${id}/read`, { method: 'POST' });
  },
  markNotificationUnread(id: string) {
    return request<{ status: string }>(`/api/notifications/${id}/unread`, { method: 'POST' });
  },
  markAllNotificationsRead() {
    return request<{ marked_read: number }>('/api/notifications/read-all', {
      method: 'POST',
    });
  },
};

// --- Notification types -------------------------------------------------

export interface Notification {
  id: string;
  user_id?: string;
  // Stable discriminator (e.g. 'submission.completed') the bell maps to an
  // icon + route; unknown types fall back to generic info.
  type: string;
  title: string;
  body?: string | null;
  // Frontend path to navigate to on click. Optional.
  link?: string | null;
  // Trigger-specific payload (task_id, target_name, status, ...).
  metadata?: Record<string, unknown> | null;
  read_at?: string | null;
  created_at: string;
}

// --- Types --------------------------------------------------------------

// InviteLookup mirrors the /api/invites/lookup response. An expired/consumed
// invite is HTTP 410 (an ApiError), not a body flag - callers distinguish it
// by status. org_id/org_name are empty for an admin (org-less) invite.
//
// It says nothing about whether the invited address already has an account, and
// the accept page must not ask: the endpoint is unauthenticated and answering
// that question for any address handed back the account-existence answer login
// goes to real trouble to deny. `existing_user` was declared here for a server
// that stopped sending it, so the branch reading it silently became dead. The
// accept RESPONSE carries `existing_account`, which is the same fact learned
// one form submission later and behind the token.
export interface InviteLookup {
  email: string;
  kind: 'user' | 'admin';
  role: 'member' | 'manager' | 'org_admin';
  expires_at: string;
  org_id?: string | null;
  org_name?: string | null;
}

// InviteCreated is the copy-link response from an invite send: the raw token
// (shown once) + the SPA accept path. The client builds the absolute URL from
// window.location.origin - no email plane, no server-configured public URL.
export interface InviteCreated {
  invite_id: string;
  email: string;
  token: string;
  accept_path: string;
  expires_at?: string;
}

export interface OrgAPIKey {
  id: string;
  org_id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at?: string | null;
  created_by?: string | null;
  created_at: string;
  revoked_at?: string | null;
}

/** PlatformKey is an operator credential for the /v1 fleet and org-lifecycle
 *  surface. It has no org_id: the operations it authorizes belong to the
 *  platform rather than to a tenant, which is the whole reason it is a separate
 *  credential from OrgAPIKey. */
export interface PlatformKey {
  id: string;
  name: string;
  key_prefix: string;
  scopes: string[];
  last_used_at?: string | null;
  created_by?: string | null;
  created_at: string;
  revoked_at?: string | null;
}

/** PLATFORM_KEY_SCOPES is the closed vocabulary the server validates against,
 *  ordered least to most consequential. There is deliberately no default set:
 *  an org key's scopes are bounded by its tenant either way, and these are not,
 *  so the operator has to say what the credential is for. */
export const PLATFORM_KEY_SCOPES = [
  { id: 'fleet:read', blurb: 'Read providers, machines, images and fleet load' },
  { id: 'platform:read', blurb: 'Read storage config and the platform audit feed' },
  { id: 'orgs:read', blurb: 'Read organizations, their members and activity' },
  { id: 'metering:read', blurb: 'Read what each sample consumed, for cost and billing' },
  { id: 'logs:read', blurb: 'Export trace logs from every component' },
  { id: 'accounts:signin', blurb: 'Check app-managed account passwords at sign-in' },
  { id: 'fleet:write', blurb: 'Provision, reprovision and destroy machines' },
  { id: 'fleet:setup', blurb: 'Run hypervisor setup - changes the host itself' },
  { id: 'fleet:setup:modify', blurb: 'Let setup change resources you already own, not just create its own' },
  { id: 'accounts:write', blurb: 'Create app-managed accounts and redeem their sign-up invites' },
  { id: 'orgs:write', blurb: 'Create and edit organizations, quotas and contracts' },
  { id: 'submissions:delete', blurb: "Delete any organization's finished analyses" },
] as const;

/** One file inspection found inside a staged upload: an archive member, a
 *  payload carved out of another file, or the upload itself. */
export interface StagedEntry {
  id: string;
  parent_id?: string;
  depth: number;
  /** Display metadata. Never a path - entries are addressed by id. */
  name: string;
  detected_format?: string;
  description?: string;
  size: number;
  /** "root" | "container" | "carved" | "fetched". A carved entry is a finding:
   *  a payload nothing in the archive declared. A fetched one was retrieved
   *  from a URL the sample pointed at, so nothing in the upload contains it. */
  origin: string;
  source_offset?: number;
  is_container: boolean;
  encrypted: boolean;
  /** False when the entry has no readable bytes, so it cannot be detonated. */
  selectable: boolean;
  unavailable_reason?: string;
  /** "program" | "library" | "driver" for a Windows binary, read from the
   *  file's own header rather than its name. Absent for anything else. */
  pe_kind?: string;
  /** The entry the server picked as the default: the program this upload is
   *  really about. */
  recommended?: boolean;
  /** A detonation the analyst is being ASKED about rather than given: a payload
   *  a reference retrieved that the guest has a detonator for. Never
   *  pre-selected - following the link already told the actor their lure
   *  landed, and running what came back is the analyst's call. */
  offered?: boolean;
  /** Why detonating this entry would not start anything. Absent when it would. */
  launch_note?: string;
  /** An analysis this org has already finished of these exact bytes, whatever
   *  they arrived inside, so the default answer to a file we have seen is the
   *  report that exists rather than a second detonation. Re-analysing stays
   *  available and is the submitter's to choose. */
  prior_analysis?: StagedPriorAnalysis;
}

/** The existing report a submitter is shown instead of being sent to run a file
 *  again. */
export interface StagedPriorAnalysis {
  submission_id: string;
  verdict?: string;
  family?: string;
  completed_at: string;
}

/** One reference a staged entry points at: a URL, a launch target, or a script
 *  that reaches one. Where it points is found locally and offline; whether it
 *  was FOLLOWED is fetch_state, and the request is issued by a provider host,
 *  never by the control plane. */
export interface StagedLink {
  id: string;
  /** The entry that carried it, so a link inside an archive member is
   *  attributed to that member. */
  entry_id: string;
  /** The mechanism it acts through. "open_action" fires with no interaction. */
  kind: 'uri' | 'open_action' | 'javascript' | 'launch' | 'goto_remote' | 'relationship';
  /** The target exactly as the sample wrote it, not defanged: the UI defangs
   *  for display. Never render it as a live anchor. */
  url?: string;
  /** Where in the document it was found, as provenance for an analyst. */
  location: string;
  /** A bounded excerpt of a script reference's source. */
  snippet?: string;
  /** Whether the target is retrievable over the network. Most are not. */
  fetchable: boolean;
  /** What following it produced. "not_attempted" means nobody tried - the
   *  target is not fetchable, or the org does not follow references. The three
   *  are different findings about a lure and only the first is silence. */
  fetch_state:
    | 'not_attempted'
    | 'pending'
    | 'fetched'
    | 'refused'
    | 'failed';
  /** Why a fetch produced nothing, in words fit for a reader. */
  fetch_reason?: string;
  /** Where the bytes actually came from, after redirects. The URL in the
   *  document may only have been the first hop. */
  final_url?: string;
  /** The tree row the retrieved bytes became. */
  fetched_entry_id?: string;
}

/** A sample uploaded but not yet submitted. It carries no score and no
 *  verdict: nothing has run. */
export interface StagedUpload {
  id: string;
  /** "uploaded" | "inspecting" | "following" | "inspected" | "needs_password" |
   *  "inspect_failed" | "consumed" | "expired". "following" is the phase where
   *  the references are being retrieved; the tree is already readable. */
  status: string;
  kind: string;
  target_name: string;
  sample_sha256: string;
  sample_size: number;
  detected_format?: string;
  needs_password: boolean;
  /** Whether a password is held - never what it is. */
  has_password: boolean;
  entries_truncated: boolean;
  state_reason?: string;
  submitted_at: string;
  expires_at: string;
  /** The server's fan-out ceiling, so the picker does not hardcode one. */
  max_picks: number;
  entries: StagedEntry[];
  /** References the entries point at, as opposed to the bytes they contain.
   *  Separate from entries because a link has no digest and nothing to
   *  detonate. */
  links: StagedLink[];
}

/** One detonatable type and the extensions that name it. */
export interface SupportedFileType {
  kind: string;
  label: string;
  extensions: string[];
}

/** Supported types grouped for display, plus the archive formats the platform
 *  unpacks so an analyst can pick a file from inside one. Derived server-side
 *  from the same tables intake gates on, so it cannot advertise what would be
 *  refused. */
export interface SupportedFileTypes {
  groups: Array<{ id: string; label: string; types: SupportedFileType[] }>;
  archives: { label: string; note: string; formats: Array<{ name: string; label: string }> };
}

/** What one staged upload produced: one queued analysis per picked entry. */
export interface StagedProfileResult {
  staged_upload_id: string;
  submissions: Array<{
    submission_id: string;
    entry_id: string;
    target_name: string;
    sample_sha256: string;
    sample_size: number;
  }>;
}

export interface Submission {
  id: string;
  malbox_task_id: string;
  target_name: string;
  /** What was submitted: 'file' or 'url'. It decides which of the fields
   *  around it carry anything - a URL submission has no sample, no size and no
   *  platform, and its `detonation_kind` is the target path's extension rather
   *  than anything the run resolved. Absent only on a response older than the
   *  column, which is a file submission by definition. Not the lane: branch on
   *  `lane` for anything about how the run happened. */
  target_kind: string;
  /** The runner kind the analysis runs on, VM_LANE or CONTAINER_LANE, written
   *  at intake and carried on every row. This is the field to branch on for
   *  what the run has (a guest to watch, an early finish, telemetry panes).
   *  Defaulted to VM_LANE when the server sent none, which is what every row
   *  older than the lane actually is. */
  lane: string;
  /** The link that was analysed. Set only on a 'url' submission. */
  target_url?: string;
  /** The analyzer that ran it. Set only on a 'url' submission. */
  analyzer?: string;
  sample_size: number;
  platform: string;
  bare_metal: boolean;
  /** The guest the run is pinned to ("windows11"), resolved and stamped at
   *  intake. Absent only when nothing could be resolved - no base image was
   *  provisioned when the submission was accepted. */
  os_family?: string | null;
  // null when stale=true (gateway couldn't reach malbox at list time);
  // treat null/stale as "refreshing", not a terminal "unknown" verdict.
  status: 'pending' | 'initializing' | 'running' | 'uploading' | 'stopping' | 'completed' | 'failed' | 'canceled' | 'unknown' | null;
  stale?: boolean;
  /** The run budget configured at submission, in seconds. The wire has always
   *  carried it on list rows (the same projection the detail endpoint reads);
   *  it was simply untyped here until the list started showing it. */
  timeout_seconds?: number | null;
  submitted_at: string;
  completed_on?: string | null;
  /** The owning organization. On the wire for every row and the detail; the
   *  report page reads it to fetch that org's logo for the printed document,
   *  because the active-org picker says 'all' for an admin. */
  org_id?: string;
  org_name?: string | null;
  sample_sha256?: string;
  // What the run CONCLUDED, from its report. Absent until the post-analysis
  // DAG writes one, which is a different state from a finished run that
  // reached no verdict - a queued or running submission has not been judged,
  // and drawing it as clean would be a claim nothing made.
  verdict?: string | null;
  confidence?: number | null;
  family?: string | null;
  /** Short marks naming what the run CONTAINED - explicit imagery, a QR
   *  destination - beside what it concluded.
   *
   *  Written by the analyzer through the result envelope, so a list can show
   *  them without loading the analyzer's document. Never a second verdict and
   *  never coloured as one: a page can be clean and still be something a
   *  reader wants warning of before they open it. Absent on every run whose
   *  analyzer named none, and on every virtual-machine run. */
  tags?: RunTag[] | null;
  /** The type the guest launches this sample as, short form ("EXE", "VBS",
   *  "XLSX"). Resolved server-side by the same call dispatch makes, so it
   *  cannot disagree with the run - and it is not the filename's extension: a
   *  hash-named sample, or one whose content overrode a misleading name, is
   *  launched as something the name never said. Absent when nothing claims
   *  the sample. */
  detonation_kind?: string;
  /** The surface the submission entered through: 'manual' (the dashboard),
   *  'api' (a direct /v1 caller), 'mcp' or 'a2a' (the agent faces), stamped
   *  once at intake. Absent on a row older than the column, which recorded
   *  none; the page shows nothing for it rather than guessing. */
  intake_channel?: string | null;
  // Set only on a file-hash search: why this row matched the queried hash.
  matched_via?: 'sample' | 'dropped' | null;
}

/** The server's own default page size for a submissions list. Named here
 *  because two surfaces ask for the first page — the dashboard's recent table
 *  and the submissions page — and one shared name is what makes those two
 *  requests one cache entry. */
export const SUBMISSION_PAGE_SIZE = 50;

/** One page of submissions plus the counters a pager reads. Mirrors the
 *  published /v1 list envelope, so the dashboard and the API can page the
 *  same way and a reader can tell a full page from the end of the list. */
export interface SubmissionPage {
  submissions: Submission[];
  /** How many submissions match the query across every page — not this
   *  page's length. */
  total: number;
  /** The page size and offset the server actually used. It clamps `limit`,
   *  so page forward with these rather than with what was asked for. */
  limit: number;
  offset: number;
  /** Whether another page follows after this one. */
  has_more: boolean;
}

export interface TaskDetail {
  id: string;
  status: string;
  target: string;
  platform: string;
  timeout: number;
  created_on: string;
  /** When the scheduler picked the task up; null while still pending.
   *  completed_on - started_on = analysis-only duration (excludes queue wait). */
  started_on: string | null;
  /** Whether the run began on an already-warm machine and so skipped the boot.
   *  null is "not observed" - still queued, a compute host too old to report
   *  it, or a container run, which has no machine to warm. Keep that distinct
   *  from false: reading null as a cold boot states a fact nobody measured. */
  started_warm: boolean | null;
  completed_on: string | null;
  /** When an operator finished the run early; null when it ended on its own.
   *  Without it a short capture is indistinguishable from one that timed out. */
  stopped_early_on: string | null;
  /** Display name of whoever stopped it; null when that account is gone. */
  stopped_early_by: string | null;
  /** Why a completed result is bounded: the run's window ran out, so the
   *  capture stops there. null on a run that finished on its own terms. It is
   *  NOT a failure - the status is 'complete' and `failure` is absent. */
  result_caveat: string | null;
  /** The first attempt, when the platform stopped it partway and the run was
   *  started once more. Everything else on the task is the second attempt.
   *  null on every run that ran once. */
  first_attempt: { failure_message: string; stopped_on: string | null } | null;
  /** The content identity of the analyzer image that produced this report,
   *  stamped at dispatch by the host that resolved it.
   *
   *  null on every file submission, which has no analyzer, and on a container
   *  run whose host could not answer. null means "not recorded" and is never a
   *  claim that no image ran, so a surface may show it or say nothing - never
   *  render its absence as a fact either way. */
  analyzer_image_digest: string | null;
  /** What was submitted, 'file' or 'url'. Defaulted to 'file' when the server
   *  sends none, matching the column's own default. Not the lane. */
  target_kind: string;
  /** The runner kind the analysis runs on, VM_LANE or CONTAINER_LANE. The
   *  field every lane-aware surface branches on. Defaulted to VM_LANE when the
   *  server sends none, which is what every row older than the lane is. */
  lane: string;
  /** The guest the run was pinned to; null when it named none. */
  os_family: string | null;
  base_image_name: string | null;
  /** The ISO 3166-1 alpha-2 country the run's traffic to its target was asked
   *  to leave from, as intake stored it. null on a run nothing routed. The
   *  REQUEST, not the exit a gateway observed. */
  exit_country: string | null;
  /** A routed file analysis's routing record: the country asked for, the
   *  path taken, each fallback with its reason and when a route dropped. null
   *  on every other run. */
  routing: TaskRouting | null;
  /** How the guest was asked to start the sample: the command line for an
   *  executable, the export and arguments for a DLL. null on a run that took
   *  the sandbox's own defaults, which is most of them. */
  launch_options: {
    exe?: { args?: string; args_parsed?: string[] };
    dll?: { export?: string; args?: string };
  } | null;
  sample?: {
    file_size: number;
    /** What the guest launches the sample as, e.g. "EXE", "VBS", "XLSX". */
    file_type: string;
    sha256: string;
  };
  // Structured failure metadata, present iff status == 'failed'. `message`
  // is user-facing; `diagnostics` is open-shape JSON (machine_*, etc.).
  failure?: TaskFailure;
  result_zip_size_bytes?: number;
}

export interface TaskFailure {
  stage: TaskFailureStage;
  message: string;
  diagnostics: Record<string, unknown>;
}

// UsageView is the shared shape every usage/quota endpoint returns
// (org-portal /usage, dashboard quota, API-key quota). usage_limit 0 means
// unlimited; remaining is only meaningful when usage_limit > 0. queued and
// running are the org's own live activity counts (server-derived - the
// paginated submissions list undercounts past its newest page).
/**
 * One analyst action against a running analysis.
 *
 * A closed vocabulary rather than a passthrough: this is the one call in the
 * dashboard that reaches a browser aimed at a hostile page, and the server
 * refuses any field it did not enumerate.
 */
export interface AnalysisInput {
  kind:
    | 'pointer_move'
    | 'pointer_down'
    | 'pointer_up'
    | 'wheel'
    | 'key_down'
    | 'key_raw_down'
    | 'key_up'
    | 'text'
    | 'navigate'
    | 'reload'
    | 'dialog'
    | 'resize';
  /** A dialog's answer: whether it was accepted, and what a prompt() gets. */
  accept?: boolean;
  prompt_text?: string;
  /** The CSS viewport a resize asks the page to adopt. */
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  click_count?: number;
  delta_x?: number;
  delta_y?: number;
  /** Chrome's modifier mask: alt 1, ctrl 2, meta 4, shift 8. */
  modifiers?: number;
  button?: 'none' | 'left' | 'middle' | 'right' | 'back' | 'forward';
  key?: string;
  code?: string;
  key_code?: number;
  text?: string;
  url?: string;
}

export interface UsageView {
  max_sample_size_bytes: number;
  bare_metal_allowed: boolean;
  usage_period: UsagePeriod;
  submissions_per_year: number;
  usage_limit: number;
  used: number;
  remaining: number;
  over_limit: boolean;
  overage_policy: OveragePolicy;
  queued: number;
  running: number;
}

// The admin org row, taken from the generated wire type rather than restated:
// a hand-written copy is where a server-side field goes missing unnoticed, and
// retention_days was missing here for exactly that reason. The two enum columns
// are narrowed because tygo emits Go's `string` for both.
//
// submissions_per_year is contract volume (0 = not set); the per-window limit
// is derived from it rationed over usage_period.
export type Org = Omit<GenOrg, 'usage_period' | 'overage_policy'> & {
  usage_period: UsagePeriod;
  overage_policy: OveragePolicy;
};

// Input to updateOrgQuotas - the operational guardrails + metering window
// + overage policy + how long the org's data survives.
//
// Every field is optional because the endpoint merges: an absent key tells the
// handler to leave that column alone. Send only what the caller changed -
// echoing a value read when the page loaded overwrites whatever another admin
// has set since. The handler refuses a body carrying no fields at all.
//
// retention_days is the one that also needs null, because it has three states
// rather than two: absent leaves the bound alone, `null` is keep forever, and a
// number is a bound in days. The handler refuses 0 and negatives with a 400.
export interface OrgLimitsInput {
  max_sample_size_bytes?: number;
  bare_metal_allowed?: boolean;
  usage_period?: UsagePeriod;
  overage_policy?: OveragePolicy;
  retention_days?: number | null;
}

export interface OrgStats {
  member_count: number;
  submissions_total: number;
  submissions_last_24h: number;
  submissions_last_7d: number;
  bytes_ingested_total: number;
  last_submission_at: string;
  last_member_login_at: string;
}

export interface OrgMember {
  org_id: string;
  user_id: string;
  role: 'member' | 'manager' | 'org_admin';
  email: string;
  display_name: string;
  is_admin: boolean;
  last_login_at: string | null;
}

/** Which organizations the admin list returns. */
export type OrgListStatus = 'active' | 'archived' | 'all';

/** One page of the admin org list, and how many orgs match in all. */
export interface OrgPage {
  orgs: Org[];
  total: number;
  limit: number;
  offset: number;
}

/** What a runner row actually runs. A container runner has no image, no
 *  hypervisor observation and no size of its own - the analyzer registry row
 *  carries its size, and the provider reads it at dispatch. */
export type RunnerKind = 'vm' | 'container';

export interface Machine {
  id: string | null;
  kind: RunnerKind;
  /** The analyzer a container runner serves. Null for a virtual machine. */
  analyzer: string | null;
  /** Why a held machine is held (null unless status is 'quarantined'). */
  quarantine_reason: string | null;
  /** When the fleet reconciler deletes a held machine, null when it is kept
   *  until an operator acts. Set for a cloud VM held for its own faults. */
  hold_expires_at: string | null;
  /** What the provider last reported. Null when it could not be reached. */
  observed_power: string | null;
  observed_provider_ref: string | null;
  /** Whether this machine is pre-reverted and past its guest self-check, so the
   *  next analysis on it starts without a boot. Null on the same terms as the
   *  two above: nobody reached its provider, so nobody knows. */
  warm: boolean | null;
  name: string;
  label: string | null;
  /** The guest's architecture and OS. Both null for a container runner, which
   *  has no guest of its own to report them for. */
  arch: string | null;
  platform: string | null;
  os_family: OSFamily | null;
  ip: string | null;
  tags: string[];
  status: string;
  image_id: string | null;
  image_name: string | null;
  provider: string | null;
  provider_id: string | null;
  current_task_id: string | null;
  error_message: string | null;
  /** The agent bundle the machine booted; null when nothing recorded one. */
  agent_bundle: AgentBundle | null;
  /** How that bundle compares with the one the provider serves now. Absent for a
   *  container runner, which boots none, and on a provider that serves none. */
  agent_bundle_status?: AgentBundleStatus;
  cpus: number | null;
  memory_mb: number | null;
  disk_size_mb: number | null;
  last_seen: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export const SUPPORTED_OS_FAMILIES: ReadonlySet<OSFamily> = new Set([
  'windows11',
  'windows10',
]);

export const OS_FAMILY_OPTIONS: { value: OSFamily; label: string }[] = [
  { value: 'windows11', label: 'Windows 11' },
  { value: 'windows10', label: 'Windows 10' },
  { value: 'windows7', label: 'Windows 7' },
  { value: 'debian', label: 'Debian' },
  { value: 'ubuntu', label: 'Ubuntu' },
  { value: 'redhat', label: 'Red Hat' },
  { value: 'azurelinux', label: 'Azure Linux' },
  { value: 'linux', label: 'Linux (other)' },
  { value: 'freebsd', label: 'FreeBSD' },
  { value: 'macos', label: 'macOS' },
];

const OS_FAMILY_LABELS: Record<OSFamily, string> = Object.fromEntries(
  OS_FAMILY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<OSFamily, string>;

/** osFamilyLabel renders an os_family token as its product name ("windows11" →
 *  "Windows 11"). A token this build doesn't know is returned verbatim, so a
 *  guest added server-side still names itself rather than disappearing. */
export function osFamilyLabel(family: string): string {
  return OS_FAMILY_LABELS[family as OSFamily] ?? family;
}

// A hypervisor snapshot for one machine, listed live from its provider
// (snapshotItem in admin_inflight.go).
export interface MachineSnapshot {
  id: string;
  name: string;
  created_at: string;
}

// ProvisionRun - one build attempt against a machine (provisionItem in
// admin_inflight.go, backed by provision_runs). `ended_at` unset means the
// run is still going; once it lands, `success` is the verdict. `log_excerpt`
// carries the provisioner log tail - the failure reason on failed runs -
// and is omitted when empty.
export interface ProvisionRun {
  id: string;
  started_at: string;
  ended_at?: string | null;
  profile: string;
  success: boolean;
  snapshot_taken: boolean;
  log_excerpt?: string;
}

export interface AuditEvent {
  id: number;
  actor_id: string | null;
  org_id: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: any;
  created_at: string;
}

// --- Report envelope (mirrors malbox-plugin-sdk types::report) ---------

export interface Verdict {
  classification: Classification;
  score?: number;
  confidence?: Confidence;
  labels?: string[];
}

export interface Section {
  id: string;
  title: string;
  blocks?: Block[];
}

export type Block =
  | { type: 'markdown'; text: string }
  | { type: 'callout'; level: 'info' | 'success' | 'warn' | 'error'; text: string }
  | { type: 'heading'; level: number; text: string }
  | { type: 'kv'; pairs: { key: string; value: string; mono?: boolean }[] }
  | { type: 'table'; columns: { key: string; label: string; type?: string }[]; rows: any[]; sortable?: boolean; searchable?: boolean }
  | { type: 'code'; language: string; text: string }
  | { type: 'json'; data: any; collapsed?: boolean }
  | { type: 'iocs'; items: Indicator[] }
  | { type: 'ttps'; items: Ttp[] }
  | { type: 'download'; artifact: string; label: string };

export interface Report {
  schema_version: number;
  plugin: { id: string; version: string; display_name?: string };
  verdict?: Verdict;
  indicators?: Indicator[];
  artifacts?: { result_name: string; kind: string; description?: string }[];
  summary?: string;
  sections?: Section[];
  behavior?: Behavior;
  // v2 addition - present from schema_version >= 2 reports.
  capture?: Capture;
}

// --- Findings (v2 canonical) ------------------------------------------
// Findings are the source of truth for what the report communicates;
// indicators[]/ttps[] are derived flat views. UI groups them by category.

export type FindingCategory =
  | 'execution'
  | 'persistence'
  | 'privilege_escalation'
  | 'defense_evasion'
  | 'credential_access'
  | 'discovery'
  | 'lateral_movement'
  | 'collection'
  | 'exfiltration'
  | 'command_and_control'
  | 'impact'
  // The rest of the ATT&CK tactics the rule packs tag with. Kept in step with
  // FindingCategory in api-types.gen.ts.
  | 'reconnaissance'
  | 'resource_development'
  | 'initial_access'
  | 'anti_analysis'
  // Not ATT&CK tactics: the job-side categories the ml and llm stages emit.
  | 'evasion'
  | 'ml_attribution'
  | 'reliability'
  | 'info';

export interface Evidence {
  kind: EvidenceKind;
  summary: string;
  detail?: unknown;
  pid?: number | null;
  process?: string | null;
  timestamp?: string | null;
}

/**
 * What narrows one page of the run's chronology.
 *
 * Every field is applied by the SERVER. That is the difference from the
 * arrangement this replaced, where a precomputed log was downloaded whole and
 * filtered in the browser: a filter that runs here answers over the whole
 * capture, and one that ran there could only answer over whatever had fit in the
 * artifact.
 */
export interface EventStreamFilter {
  /** Empty means every source, not none - a reader who cleared the filter is
   *  asking for the whole chronology. */
  sources?: readonly string[];
  /** Narrow to one process. Matched against the ATTRIBUTED pid, so a service
   *  the sample created is filed under the sample rather than services.exe. */
  pid?: number | null;
  /** Matched case-insensitively against an event's name, image and payload.
   *  Treated as a literal, so a typed `%` finds a `%`. */
  search?: string;
  /** Resume after a previous page's last event. */
  cursor?: EventStreamCursor | null;
  limit?: number;
}

export interface Finding {
  id: string;
  title: string;
  severity: number;
  category: FindingCategory;
  description?: string | null;
  evidence: Evidence[];
  techniques: string[];
  indicators: Indicator[];
  confidence: Confidence;
  plugin?: string;
}

// --- Capture (reliability metadata) -----------------------------------

export interface Capture {
  completeness: CaptureCompleteness;
  traced_process_count: number;
  system_events_count: number;
  sample_pid_observed: boolean;
  sample_exit_observed: boolean;
  first_event_at?: string | null;
  last_event_at?: string | null;
  agent_errors: string[];
  notes?: string | null;
}

// --- Behavior (the report's behavior section) --------------------------
// Mirrors the trace extraction the ml stage derives (voltz_job_ml/behavior.py)
// and the orchestrator serves as the flat `behavior` section, so the report
// tabs render without walking raw ETW events. No ATT&CK anywhere in it:
// technique attribution is the matcher's document alone.

export interface ProcessNode {
  pid: number | null;
  ppid: number | null;
  name: string;
  image_path: string;
  command_line: string;
  start_time?: number | string | null;
  end_time?: number | string | null;
  process_name?: string;
  /**
   * The kernel's PromotionReason: why this process was monitored. "Snapshot"
   * means it was merely alive when the driver attached; MainSample, Tree,
   * InjectionTarget, ScheduledTaskChild, WmiChild, ComSurrogateChild and
   * DroppedFileLoaded all mean the sample caused it. Absent on archives
   * written before the ml job carried it through - see monitoredScope.ts.
   */
  reason?: string;
  // Measured lifetime and time spent in NtDelayExecution. Absent when the
  // capture recorded no exit or no delay, which is why both are optional:
  // "not observed" and "zero" are different answers and must not collapse.
  duration_ms?: number | null;
  sleep_ms?: number | null;
  sleep_calls?: number | null;
  children: ProcessNode[];
}

/** How many events an entry stands for.
 *
 *  The ml job emits one entry per DISTINCT thing observed, not per event: a
 *  document sized by how long the sample ran overflowed the worker's frame cap
 *  and cost a whole submission. Optional because a document written before that
 *  change carries one entry per event and no count - `?? 1` reads both. */
type Folded = { count?: number };

export interface DnsQuery extends Folded {
  query_name: string;
  query_type?: string;
  results: string[];
  status?: number;
  success?: boolean;
  pid?: number | null;
  process?: string;
  /** When the FIRST of the folded run was recorded. */
  timestamp?: number | string | null;
}

export interface NetworkSocket extends Folded {
  address_family: string;
  protocol?: string;
  socket_type?: string;
  pid?: number | null;
  process?: string;
  timestamp?: number | string | null;
}

export interface NetworkDestination {
  host: string;
  ips: string[];
  query_types: string[];
  contact_count: number;
  processes: string[];
  // Geolocation, present only when the analysis host carries a GeoIP
  // database. Absent for private ranges and for hosts it could not resolve,
  // which the map reports rather than silently omitting.
  country_code?: string;
  country_name?: string;
  lat?: number;
  lon?: number;
}

export interface FileOp {
  path: string;
  operation: string;
  timestamp?: number | string | null;
}

export interface RegistryOp {
  operation: string;
  key: string;
  status?: number;
  timestamp?: number | string | null;
}

export interface SuspiciousCall extends Folded {
  event: string;
  provider: string;
  /** How notable the call is on its own (0-100). Not an ATT&CK claim - the
   *  per-entry technique ids the extractor used to stamp here labelled every
   *  image load as injection, and the server strips them from archived
   *  documents too. */
  severity: number;
  /** The process this call acted on, when it acted on another one.
   *  Read from the shared schema's promoted target_pid column - it is NOT a
   *  `fields` key, which is why the raw blob this replaced never carried it. */
  target_pid?: number | null;
  pid?: number | null;
  process?: string;
  timestamp?: number | string | null;
}

export interface PersistenceHit extends Folded {
  kind: string;
  label: string;
  evidence: string;
  pid?: number | null;
  process?: string;
  timestamp?: number | string | null;
}

/** The matcher's technique shape, as MitreTab's fold decorates it into the
 *  ATT&CK matrix (see MatrixTechnique). */
export interface MitreTechnique {
  id: string;
  name: string;
  tactic: string;
  evidence: string[];
  evidence_count: number;
}

export interface Behavior {
  process_tree: ProcessNode[];
  processes: ProcessNode[];
  network: {
    dns: DnsQuery[];
    sockets: NetworkSocket[];
    destinations: NetworkDestination[];
    /** The query log was clipped to keep the document inside the worker's
     *  frame cap. The sections that read DNS as sets and maps are unaffected;
     *  the DNS Queries list says so rather than ending early in silence. */
    dns_truncated?: boolean;
  };
  /**
   * Per-process file and registry totals. Counts only: the operations behind
   * them live in the run's shared event stream, which the activity panes query
   * in full, so carrying them here as well made this the largest part of the
   * report to answer questions that only need a total.
   */
  ops_by_pid: Record<string, { files: number; registry: number }>;
  suspicious_calls: SuspiciousCall[];
  persistence: PersistenceHit[];
  summary_stats: {
    process_count: number;
    dns_query_count: number;
    socket_count: number;
    file_op_count: number;
    registry_op_count: number;
    suspicious_count: number;
    persistence_count: number;
  };
}

// Exactly what the report page reads off the aggregate - the confidence and
// engine bookkeeping it used to carry were dead weight nobody consumed, held
// in place by an unsound cast.
interface AggregateView {
  verdict?: Classification;
  indicators: Indicator[];
}

interface PluginReportView {
  plugin_name: string;
  report: Report | null;
}

/**
 * One downloadable entry of a run's result archive, with the URL that fetches
 * it. What `api.artifactList` returns and what every artifact-reading pane
 * takes.
 */
export interface Artifact {
  result_name: string;
  format: string;
  size_bytes: number;
  url: string;
}

// The analyst narrative from the `llm` post-analysis job. Absent when that job
// did not run, is disabled, or reached no conclusion - it is a soft-edged,
// operator-disableable stage, so absence is ordinary and the report renders
// without an Analyst tab.
export interface AnalystFinding {
  title: string;
  detail?: string;
  severity?: string;
  evidence_refs?: string[];
}

/** An indicator the model selected, with the reason it chose it.
 *
 *  Distinct from the extracted indicator set: that is every value the run
 *  touched, this is the subset worth acting on. `why` is optional because a
 *  model that omitted it still yields a usable row. */
export interface AnalystIOC {
  kind: string;
  value: string;
  why?: string;
  candidate_id?: string;
  hash_algorithm?: string;
  origin?: 'analyst_selected' | 'analyzer_selected' | 'observed';
  actionability?: 'hostile' | 'not_assessed';
}

/** A string the model judged meaningful, with its reason. Distinct from the
 *  deterministic string dumps: those are the measurement, this is the reading
 *  of it. */
export interface AnalystString {
  value: string;
  why: string;
  pid?: string;
  scan_type?: string;
}

/** What one peer was for, on the Network tab's infrastructure table. The role
 *  vocabulary is closed on the producer side (c2, payload_host, exfil_target,
 *  resolver, benign_service, unknown); anything else never reaches the wire. */
export interface NetworkPeerRole {
  /** The peer exactly as the run records it - an address or a host. */
  peer: string;
  role: string;
  /** One line: what was observed that assigns this role. */
  note?: string;
}

/** The Network tab's structured verdict from the llm job, beside the profile
 *  paragraph. Absent on reports produced before the field existed. A
 *  classification of "none" is a judgement that the sample was quiet -
 *  distinct from the field being absent. */
export interface NetworkAssessment {
  /** Closed vocabulary: c2_beaconing, exfiltration, download_staging,
   *  reconnaissance, dead_end, benign_noise, none. Unknown values render
   *  neutrally. */
  classification: string;
  /** 0–1: how strongly the traffic supports the classification. */
  confidence: number;
  /** periodic | burst | continuous | none | '' (the model gave none). */
  cadence: string;
  /** Median beacon interval in seconds; only set when cadence is periodic. */
  interval_seconds: number;
  peer_roles: NetworkPeerRole[];
}

/** One deterministic C2-shape detection, persisted by the llm job at finalize
 *  whether or not the model quoted it. `fields` carries the structured values
 *  the tab renders - destination, contacts, median_interval_s, jitter, zone,
 *  unique_names, framework, samples - keyed per `kind`. */
export interface NetworkDetection {
  /** c2.beaconing | c2.dga | c2.dns_tunnelling.* | c2.uri.* */
  kind: string;
  severity: string;
  summary: string;
  detail: string;
  pid: number;
  image: string;
  fields: Record<string, unknown>;
}

/** The deterministic detection block. An absent block means the detector did
 *  not run (old report, or a detector failure); an empty findings list means
 *  it ran and found nothing - the tab renders those differently. */
export interface NetworkDetections {
  findings: NetworkDetection[];
  negatives_checked: string[];
}

/** One exact captured value an action item applies to. */
export interface ActionTarget {
  kind: ActionTargetKind;
  value: string;
  candidate_id: string;
  hash_algorithm?: string;
}

export interface ActionReferences {
  attack_mitigations: string[];
  d3fend_tactic: string;
}

/**
 * One thing to do next, from the `llm` job's action plan.
 *
 * `source` says who wrote it - the analyst model, or the platform floor for a
 * universal move the model left out. `target_role` says what the targets are
 * for: an `act` item names only what the review published as hostile, a
 * `lookup` item may name an observed value because the action is to look it
 * up, never to block it. Everything from `phase` on derives from `kind` on
 * the producer side; the vocabulary is `lib/actionItems`.
 */
export interface ActionItem {
  kind: ActionKind;
  title: string;
  why: string;
  capabilities: string[];
  targets: ActionTarget[];
  evidence_refs: string[];
  mitigation: string;
  source: ActionSource;
  phase: ActionPhase;
  priority: ActionPriority;
  applies_when: ActionAppliesWhen;
  target_role: ActionTargetRole;
  guidance: string;
  references: ActionReferences;
}

/** The verdict as a decision: what the analyst does with the alert. */
export interface ActionDisposition {
  verdict: string;
  decisive: boolean;
  title: string;
  detail: string;
}

/** Something the run could not observe, with its remedy in `detail`. */
export interface ActionLimitation {
  code: string;
  detail: string;
}

/** The Summary's action items: the frame the job wrote from the verdict and
 *  the run's facts, and the items in priority order. Absent on reports
 *  written before the field existed. */
export interface ActionPlan {
  disposition: ActionDisposition;
  limitations: ActionLimitation[];
  items: ActionItem[];
}

/** Optional peer attribution retained by historical analyst documents. */
export interface NetworkContext {
  addresses: Record<string, { attribution: string; finding_named: boolean }>;
  hosts: Record<string, string[]>;
}

export interface AnalystReport {
  verdict: string;
  confidence: number;
  family?: string;
  summary: string;
  narrative: string;
  /** The Network tab's opening paragraph: how this sample used the network,
   *  in the analyst's words. Empty - and absent on reports written before the
   *  field existed - for a run that resolved nothing and contacted nothing. */
  network_profile?: string;
  /** The structured call above that prose. Absent on old reports and when the
   *  model produced none. */
  network_assessment?: NetworkAssessment;
  /** Deterministic C2-shape detections. Absent when the detector did not run. */
  network_detections?: NetworkDetections;
  network_context?: NetworkContext;
  key_findings?: AnalystFinding[];
  mitre_techniques?: string[];
  iocs?: AnalystIOC[];
  notable_strings?: AnalystString[];
  model?: string;
  events_analysed?: number;
  truncated?: boolean;
  /** What to do next. Absent on reports written before the field existed. */
  action_plan?: ActionPlan;
}

/** One post-analysis job's result as the report page consumes it. `document`
 *  is the full envelope-bearing output, which a pane narrows to its own
 *  shape. */
// ---------------------------------------------------------------------------
// Static profile - the `static` job's document.
//
// It answers "what is this file" rather than "what did it do": what produced
// it, how it is put together, and the measurements behind both. Available even
// when detonation produced nothing, which is when an analyst needs it most.
//
// Every field is optional. A non-PE sample carries no `pe` block, and a sample
// nothing recognised carries no detections - both are ordinary results.
// ---------------------------------------------------------------------------

/** One labelled fact, in the order the job emitted it. */
export interface StaticKeyValue {
  key: string;
  value: string;
}

/** One identification. `rule` traces the claim back to the rule that made it. */
export interface StaticDetection {
  type: string;
  name: string;
  version?: string;
  options?: string;
  lang?: string;
  lang_version?: string;
  rule: string;
}

/** One sample of the entropy graph. */
export interface StaticEntropyPoint {
  offset: number;
  entropy: number;
}

/**
 * One region's entropy, with the job's reading of it against the threshold.
 *
 * `status` describes the bytes, not what produced them - compression,
 * encryption and embedded media are indistinguishable at this level. Obfuscation,
 * when one is identified, arrives as a detection under its own name.
 */
export interface StaticEntropyRegion {
  name: string;
  offset: number;
  size: number;
  entropy: number;
  status: StaticEntropyStatus;
}

export type StaticEntropyStatus = 'high' | 'normal';

/**
 * The entropy picture. `threshold` ships with the data deliberately - the UI
 * must not hardcode it, so the backend can change the reading without a
 * frontend release.
 */
export interface StaticEntropy {
  total: number;
  status: StaticEntropyStatus;
  threshold: number;
  graph?: StaticEntropyPoint[];
  byte_counts?: number[];
  // Optional like the rest of the profile: the job omits what it could not
  // compute, and a document without regions must render as absent rather
  // than crashing the tab.
  regions?: StaticEntropyRegion[];
}

/** A span of the address model. `is_virtual` marks address space no file bytes back. */
export interface StaticMemoryRecord {
  name: string;
  offset: number;
  /** null when the region occupies file bytes but no address space, as an overlay does. */
  address: number | null;
  size: number;
  is_virtual: boolean;
}

/** One X.509 certificate from the Authenticode block. */
export interface StaticCertificate {
  subject: string;
  subject_cn: string;
  issuer: string;
  issuer_cn: string;
  serial_number: string;
  thumbprint: string;
  not_before: string;
  not_after: string;
  algorithm: string;
  key_usage?: string[];
  is_ca: boolean;
  self_signed: boolean;
  expired: boolean;
  not_yet_valid: boolean;
  ocsp_servers?: string[];
  crl_points?: string[];
}

/** The Authenticode block. `verified` is never true from parsing alone - the
 *  job reports what the file carries, not whether Windows would accept it. */
export interface StaticSignature {
  present: boolean;
  verified: boolean;
  status: string;
  status_detail: string;
  signers?: StaticCertificate[];
  certificates?: StaticCertificate[];
  size_bytes: number;
}

export interface StaticSection {
  name: string;
  virtual_address: number;
  virtual_size: number;
  file_offset: number;
  file_size: number;
  characteristics: number;
  entropy: number;
}

export interface StaticImportedLibrary {
  name: string;
  functions?: string[];
  position_hash: number;
}

export interface StaticResource {
  type: number;
  id: number;
  name?: string;
  offset: number;
  size: number;
}

export interface StaticDataDirectory {
  index: number;
  name: string;
  virtual_address: number;
  size: number;
  /** -1 when the directory resolves into no section, itself a signal. */
  section: number;
}

export interface StaticRichEntry {
  id: number;
  version: number;
  count: number;
}

export interface StaticDotNet {
  runtime_version?: string;
  /** The CLI header was found by probing, which only a protector arranges. */
  header_hidden?: boolean;
  name_count: number;
  literal_count: number;
}

/** The PE structure. Absent for every other format. */
export interface StaticPE {
  machine: number;
  timestamp: number;
  characteristics: number;
  subsystem: number;
  dll_characteristics: number;
  image_base: number;
  entry_point_rva: number;
  entry_point_offset: number;
  /** e_lfanew: where the PE signature begins, and the anchor every header
   *  after the DOS one is measured from. Without it a field table can name a
   *  value but not say where in the file it lives. */
  nt_headers_offset: number;
  entry_point_section: number;
  linker_version: string;
  size_of_code: number;
  size_of_image: number;
  declared_size_of_headers: number;
  actual_size_of_headers: number;
  checksum: number;
  size_of_initialized_data: number;
  base_of_code: number;
  base_of_data?: number;
  major_os_version: number;
  minor_os_version: number;
  major_image_version: number;
  minor_image_version: number;
  major_subsystem_version: number;
  minor_subsystem_version: number;
  win32_version_value: number;
  size_of_stack_reserve: number;
  size_of_stack_commit: number;
  size_of_heap_reserve: number;
  size_of_heap_commit: number;
  loader_flags: number;
  number_of_rva_and_sizes: number;
  is_dll: boolean;
  is_driver: boolean;
  is_console: boolean;
  is_signed: boolean;
  is_dotnet: boolean;
  // Optional like every other part of the profile: the job omits what it
  // could not parse, and a document without sections must render as absent
  // rather than crashing the pane.
  signature?: StaticSignature;
  sections?: StaticSection[];
  imports?: StaticImportedLibrary[];
  exports?: string[];
  resources?: StaticResource[];
  directories?: StaticDataDirectory[];
  /** Clusters by family; survives a repack that changes every content hash. */
  imphash?: string;
  /** Clusters by build environment. Empty when the block was stripped. */
  rich_hash?: string;
  import_hash32: number;
  import_hash64: number;
  file_version?: string;
  version_strings?: StaticKeyValue[];
  has_manifest: boolean;
  debug_types?: string[];
  rich?: StaticRichEntry[];
  dotnet?: StaticDotNet;

  // The directories the static job walks out of line. Each is OPTIONAL and
  // absent when the image does not declare it - a consumer cannot tell a zero
  // security cookie from "no load config", and for a mitigation those are
  // opposite claims about how the binary was built.
  relocations?: StaticRelocations;
  tls?: StaticTLS;
  load_config?: StaticLoadConfig;
  delay_imports?: StaticDelayImport[];
  bound_imports?: StaticBoundImport[];
  exceptions?: StaticExceptions;
}

/** The base relocation table, folded: a real image has tens of thousands of
 *  entries and none is individually interesting. */
export interface StaticRelocations {
  /** Where the directory starts in the file, so a reader can reach the bytes
   *  rather than trusting the decode. */
  offset: number;
  blocks: number;
  total_entries: number;
  truncated: boolean;
  /** Entries per named type. What distinguishes a normal table from one a
   *  packer rebuilt by hand. */
  type_counts?: Record<string, number>;
}

export interface StaticTLSCallback {
  address: number;
  rva: number;
  offset: number;
  /** Empty when the callback lands outside every section, which is the case
   *  worth seeing. */
  section: string;
}

export interface StaticTLS {
  offset: number;
  start_address_of_raw_data: number;
  end_address_of_raw_data: number;
  address_of_index: number;
  address_of_callbacks: number;
  size_of_zero_fill: number;
  characteristics: number;
  /** Carried in full: these run BEFORE the entry point. */
  callbacks?: StaticTLSCallback[];
  truncated: boolean;
}

export interface StaticLoadConfig {
  offset: number;
  size: number;
  time_date_stamp: number;
  security_cookie: number;
  seh_handler_table: number;
  seh_handler_count: number;
  guard_cf_function_table: number;
  guard_cf_function_count: number;
  guard_flags: number;
  /** Whether the structure reached each field at all. A mitigation never
   *  declared and one declared zero are different claims. */
  declared_security_cookie: boolean;
  declared_seh_handler_table: boolean;
  declared_guard_flags: boolean;
  guard_flag_names?: string[];
  truncated: boolean;
}

/** A delay-loaded library. These do NOT appear in the ordinary import table. */
export interface StaticDelayImport {
  library: string;
  time_date_stamp: number;
  functions?: string[];
}

export interface StaticBoundImport {
  module: string;
  time_date_stamp: number;
  forwarder_refs?: string[];
}

export interface StaticExceptions {
  offset: number;
  entry_size: number;
  functions: number;
  handlers: number;
  chained: number;
  truncated: boolean;
  /** The machine's entry layout is unknown, so the table was NOT walked.
   *  Distinct from a table with no entries. */
  unsupported: boolean;
}

export interface StaticHashes {
  md5: string;
  sha1: string;
  sha224: string;
  sha256: string;
  sha384: string;
  sha512: string;
  crc32: number;
}

/**
 * A byte-pattern hit. Weaker evidence than a detection: it carries no logic, so
 * it cannot read a version or exclude a look-alike, and short patterns do match
 * inside compressed data. Never render these with the same weight.
 */
export interface StaticFlatSignature {
  name: string;
  category: string;
  offset: number;
  ep_only: boolean;
}

export interface StaticRuleError {
  rule: string;
  error: string;
}

export interface StaticProfile {
  schema_version: number;
  impl: string;
  summary: string;
  type: string;
  size_bytes: number;
  info?: StaticKeyValue[];
  hashes?: StaticHashes;
  detections?: StaticDetection[];
  entropy?: StaticEntropy;
  memory_map?: StaticMemoryRecord[];
  pe?: StaticPE;
  flat_signatures?: StaticFlatSignature[];
  /** Rules that failed to run, so a detection gap is visible rather than silent. */
  rule_errors?: StaticRuleError[];
}

// One report section for the pane registry. The envelope keys are present on
// every section that carries a stage's own document; the two the server
// carves out of another stage's (`behavior`, `network`) are pure measurement
// and carry none, which is why all three are optional.
export interface JobResult {
  job_name: string;
  schema_version?: number;
  impl?: string;
  summary?: string;
  document: Record<string, unknown>;
}

export interface TaskReport {
  // task is optional here: api.getReport callers obtain the task separately
  // via api.getSubmission. The report endpoint returns report+analysis data
  // only, not a task row.
  task?: TaskDetail;
  aggregate: AggregateView;
  plugins: PluginReportView[];
  // The lineage trio, present only when a sample derived from this
  // submission was judged worse. The verdict to act on is
  // effectiveVerdict ?? aggregate.verdict; the aggregate keeps the run's own
  // unrewritten observation.
  effectiveVerdict?: Classification;
  verdictSource?: string;
  verdictSourceSubmission?: string;
  // Per-job outcomes from the post-analysis DAG. Admin-only; absent for
  // regular users and when the DAG hasn't run yet.
  pa_jobs?: PAJobRun[];
  // Every succeeded post-analysis job's document, for the pane registry.
  jobResults: JobResult[];
}

// --- Provisioning options ------------------------------------------------

/** One selectable machine type, with the resources that type carries. */
interface MachineProfile {
  id: string;
  cores: number;
  memory_mb: number;
  disk_gb?: number;
  cpu_type?: string;
  architecture?: string;
}

/**
 * What the provision-machines dialog is allowed to offer for one provider.
 *
 * `sizing` decides the shape of the form: `free_form` renders vCPU/RAM inputs,
 * `profiles` renders a machine-type selector - because a profile-sized backend
 * is only ever handed a type name, so vCPU/RAM inputs would be controls it
 * never receives.
 */
interface ProvisioningOptions {
  sizing: 'free_form' | 'profiles';
  sizing_label?: string;
  machine_profiles: MachineProfile[];
  bare_metal_capable: boolean;
  requires_image_setup: boolean;
  setup_image_shas: string[];
}

// --- Hypervisor setup step status ---------------------------------------

// SetupStepStatus and SetupRunInfo used to be declared here as well as in the
// generated file, and the setup panel imported this copy. That is why the
// generated one was wrong for two months without anyone noticing: it named
// `output`, `error` and `completed_at`, which no emitter has ever written, and
// omitted the whole retry, progress and typed-failure half the handler does
// send. Two declarations of one response, and the page picked the accurate one
// by accident. There is now one, and it is the generated one.
//
// The hand-written copy also carried `elapsed_seconds`, which nothing sends
// either: there is no such column, no such domain field and no such wire key.
// The panel's elapsed badge read it and so never rendered. It is derived from
// started_at/ended_at there now.
// The preflight shapes are re-exported rather than restated, for the reason
// the comment above records: two declarations of one response let the page
// read the wrong one for two months.
export type { ConfigOption, ConfigOptionsResponse, SetupGap, SetupRunInfo, SetupStepStatus } from './api-types.gen';
export type { SetupPreflightResponse };

// HypervisorSetupStatusResponse narrows the generated envelope rather than
// restating it: apitypes marks both members `omitempty`, so tygo generates them
// optional, but the handler writes a map with both keys on every response and
// `toStepResponses` returns `[]` rather than nil. Neither is ever absent.
// Required<> keeps the field names sourced from the generated type - when the
// two tags are corrected this becomes a no-op and can go.
export type HypervisorSetupStatusResponse = Required<GenHypervisorSetupStatusResponse>;

// --- SSE event ---------------------------------------------------------

// The wire envelope, generated from internal/sse.Event: payload fields live
// inside `data`, never at the top level.
export type { SSEEvent, SSEKind } from './api-types.gen';
